
import { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// ─── Upload image to Supabase Storage ────────────────────────────────────────
const uploadImage = async (dataUrl, path) => {
  if (!dataUrl || !dataUrl.startsWith("data:")) return dataUrl; // already a URL
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const ext = blob.type.split("/")[1] || "jpg";
    const filePath = `${path}-${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage.from("playgrounds").upload(filePath, blob, { upsert: true });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from("playgrounds").getPublicUrl(filePath);
    return urlData.publicUrl;
  } catch(err) {
    console.error("Upload error:", err);
    return dataUrl; // fall back to base64 if upload fails
  }
};

// ─── Time ago helper ─────────────────────────────────────────────────────────
const timeAgo = (timestamp) => {
  if (!timestamp) return "just now";
  if (timestamp === "just now") return "just now";
  const now = new Date();
  // Handle numeric timestamps (from project ID), strings, and ISO dates
  const date = typeof timestamp === "number"
    ? new Date(timestamp)
    : new Date(typeof timestamp === "string" ? timestamp.replace(" ", "T") : timestamp);
  if (isNaN(date.getTime())) return "just now";
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 14) return "1 week ago";
  if (days < 21) return "2 weeks ago";
  if (days < 28) return "3 weeks ago";
  const months = Math.floor(days / 30);
  if (months < 2) return "1 month ago";
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

// ─── Palette ──────────────────────────────────────────────────────────────────
// #000 black · #0d0d0d card · #141414 surface · #1f1f1f border
// #2e2e2e hover border · #333 dim · #555 muted · #888 secondary
// #bbb body · #fff white / active — NO gradients, NO color

const CATEGORIES = [
  "All",
  // Motion & Video
  "Motion Design","3D Render","VFX","Video Editing",
  // Design
  "Web Design","Product / UI Design","Graphic Design","Brand & Identity","Illustration",
  // Other
  "Experiment","Tutorial",
];

// Which categories are design-first (drive smart media options in PostModal)
const CAT_TYPE = {
  "Motion Design":"motion","3D Render":"motion","VFX":"motion","Video Editing":"motion",
  "Web Design":"web","Product / UI Design":"ui",
  "Graphic Design":"graphic","Brand & Identity":"brand","Illustration":"illustration",
  "Experiment":"experiment","Tutorial":"tutorial",
};

const EMOJI_REACTIONS = ["❤️", "👍"];
const CAT_ICON = {
  "Motion Design":"✦","3D Render":"◈","VFX":"◉","Video Editing":"▶",
  "Web Design":"⬡","Product / UI Design":"▣","Graphic Design":"◐",
  "Brand & Identity":"◆","Illustration":"✐","Experiment":"◎","Tutorial":"T",
};

// ─── B&W image converter (canvas) ────────────────────────────────────────────
// Takes a base64 data URL, returns a new grayscale base64 data URL
const toGrayscale = (dataUrl) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    // Scale down to max 800px wide before processing — avoids loop limits in sandboxed environments
    const MAX = 800;
    const scale = img.width > MAX ? MAX / img.width : 1;
    const w = Math.floor(img.width * scale);
    const h = Math.floor(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const d = ctx.getImageData(0, 0, w, h);
    const len = d.data.length;
    for (let i = 0; i < len; i += 4) {
      const g = Math.round(0.299*d.data[i] + 0.587*d.data[i+1] + 0.114*d.data[i+2]);
      d.data[i] = g; d.data[i+1] = g; d.data[i+2] = g;
    }
    ctx.putImageData(d, 0, 0);
    resolve(canvas.toDataURL("image/jpeg", 0.92));
  };
  img.src = dataUrl;
});

// ─── Safe URL helper ─────────────────────────────────────────────────────────
// Ensures any user-entered URL has a protocol so it opens externally
const safeUrl = (url) => {
  if (!url || !url.trim()) return "#";
  const u = url.trim();
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return "https://" + u;
};

// ─── Video thumbnail helper ───────────────────────────────────────────────────
// Extracts a free thumbnail image URL from a YouTube or Vimeo link
const videoThumb = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/maxresdefault.jpg`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://vumbnail.com/${vimeo[1]}.jpg`;
  return null;
};

// Check if a thumbnail URL is likely a lock/error screen
// vumbnail returns a specific small image for private videos
const isValidThumb = (url) => {
  if (!url) return false;
  return true; // onError handler on img tag will catch actual failures
};


// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED_MEMBERS = [
  { id:"m1", name:"Shannon Wilkerson", initials:"SW", role:"Senior Motion Designer", bio:"", behance:"", linkedin:"", instagram:"", avatarUrl:"", avatarPosition:"50% 50%", avatarScale:1.4, bannerUrl:"", bannerPosition:"50% 50%", bannerScale:1.0, bannerOpacity:0.5, email:"Shannon@shannonwilkerson.com", username:"Deltron3030", password:"Obbaigu1!", isAdmin:true },
];

const SEED_PROJECTS = [];

// ─── Shared UI ────────────────────────────────────────────────────────────────

const F = { barlow:"'Barlow',sans-serif", cond:"'Barlow Condensed',sans-serif" };

const Avatar = ({ member, size=36 }) => {
  if (!member) return null;
  const scale = member.avatarScale || 1.4;
  const imgSize = `${scale * 100}%`;
  return member.avatarUrl
    ? <div style={{ width:size, height:size, borderRadius:"50%", border:"1px solid #2e2e2e", overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <img src={member.avatarUrl} alt={member.name}
          style={{ width:imgSize, height:imgSize, objectFit:"cover", objectPosition:member.avatarPosition||"center", pointerEvents:"none", flexShrink:0 }} />
      </div>
    : <div style={{ width:size, height:size, borderRadius:"50%", background:"#141414", border:"1px solid #2e2e2e", display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.33, fontWeight:700, color:"#bbb", fontFamily:F.cond, flexShrink:0, letterSpacing:"0.04em" }}>{member.initials}</div>;
};

const Btn = ({ onClick, children, variant="ghost", style={} }) => {
  const base = { padding:"8px 16px", border:"1px solid", fontFamily:F.cond, fontSize:11, fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s", ...style };
  const v = {
    primary: { background:"#fff", borderColor:"#fff", color:"#000" },
    ghost:   { background:"transparent", borderColor:"#2e2e2e", color:"#bbb" },
    danger:  { background:"transparent", borderColor:"#bbb", color:"#bbb" },
  };
  return (
    <button onClick={onClick} style={{ ...base, ...v[variant] }}
      onMouseEnter={e => { if(variant==="primary"){ e.currentTarget.style.background="#bbb"; e.currentTarget.style.borderColor="#bbb"; } else { e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; } }}
      onMouseLeave={e => { if(variant==="primary"){ e.currentTarget.style.background="#fff"; e.currentTarget.style.borderColor="#fff"; } else { e.currentTarget.style.borderColor=v[variant].borderColor; e.currentTarget.style.color=v[variant].color; } }}
    >{children}</button>
  );
};

const Input = ({ value, onChange, onKeyDown, placeholder, type="text", style={}, ...rest }) => (
  <input type={type} value={value} onChange={onChange} onKeyDown={onKeyDown} placeholder={placeholder}
    style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box", ...style }} {...rest} />
);

const Textarea = ({ value, onChange, placeholder, rows=4, style={} }) => (
  <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
    style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", resize:"vertical", boxSizing:"border-box", ...style }} />
);

const Label = ({ children }) => (
  <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:8 }}>{children}</div>
);

const Section = ({ children, style={} }) => (
  <div style={{ marginBottom:20, ...style }}>{children}</div>
);

const Divider = () => <div style={{ borderTop:"1px solid #1f1f1f", margin:"24px 0" }} />;

const Tag = ({ label }) => (
  <span style={{ padding:"3px 10px", border:"1px solid #2e2e2e", color:"#bbb", fontSize:11, fontFamily:F.cond, letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</span>
);

const CategoryPill = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{ padding:"5px 14px", border:"1px solid", borderColor:active?"#1d6bfc":"#2e2e2e", background:active?"#1d6bfc":"transparent", color:active?"#fff":"#bbb", fontSize:"11px", fontWeight:700, cursor:"pointer", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", transition:"all 0.15s", whiteSpace:"nowrap" }}>{label}</button>
);

// ─── Toast notification ───────────────────────────────────────────────────────

const Toast = ({ msg, onDismiss }) => (
  <div style={{ position:"fixed", top:20, right:20, zIndex:9999, background:"#141414", border:"1px solid #2e2e2e", borderLeft:"2px solid #1d6bfc", padding:"14px 18px", maxWidth:320, boxShadow:"0 8px 40px rgba(0,0,0,0.7)", animation:"slideIn 0.25s ease" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
      <div>
        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:5 }}>{msg.label}</div>
        <div style={{ fontSize:13, color:"#fff", fontWeight:700, fontFamily:F.cond, letterSpacing:"0.04em", textTransform:"uppercase", marginBottom:2 }}>{msg.title}</div>
        <div style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow }}>{msg.sub}</div>
      </div>
      <button onClick={onDismiss} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:18, padding:0, lineHeight:1 }}>×</button>
    </div>
  </div>
);

// ─── Simulated email alert modal ──────────────────────────────────────────────

const EmailPreview = ({ project, author, members, onClose }) => (
  <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:2000, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
    <div style={{ background:"#141414", border:"1px solid #2e2e2e", maxWidth:560, width:"100%", padding:"32px", boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>
      <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>Email Alert — Simulated Preview</div>
      <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:24 }}>Feedback Request Sent</div>

      {/* Email mockup */}
      <div style={{ background:"#0d0d0d", border:"1px solid #1f1f1f", padding:"20px 24px" }}>
        <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginBottom:4 }}>
          <b style={{ color:"#bbb" }}>To:</b> {members.filter(m=>m.id!==author.id).map(m=>m.name).join(", ")}
        </div>
        <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginBottom:16 }}>
          <b style={{ color:"#bbb" }}>Subject:</b> [{author.name}] is asking for feedback on "{project.title}"
        </div>
        <Divider />
        <div style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.7 }}>
          <p style={{ marginBottom:12 }}>Hey team,</p>
          <p style={{ marginBottom:12 }}><b style={{ color:"#fff" }}>{author.name}</b> has posted a new project and is requesting your feedback.</p>
          <div style={{ background:"#141414", border:"1px solid #1f1f1f", padding:"14px 16px", marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>{project.title}</div>
            <div style={{ fontSize:12, color:"#bbb", marginBottom:10 }}>{project.description}</div>
            <div style={{ fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>Challenges</div>
            <div style={{ fontSize:12, color:"#bbb", fontStyle:"italic" }}>{project.challenges}</div>
          </div>
          <p style={{ marginBottom:12, color:"#bbb" }}>Click below to view the project and leave your feedback:</p>
          <div style={{ display:"inline-block", padding:"8px 18px", border:"1px solid #fff", color:"#fff", fontSize:11, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:800 }}>View Project on Playgrounds →</div>
        </div>
      </div>

      <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end" }}>
        <Btn onClick={onClose} variant="primary">Close Preview</Btn>
      </div>
    </div>
  </div>
);

// ─── Email Confirmation modal ─────────────────────────────────────────────────

const EmailConfirmModal = ({ member, onClose }) => (
  <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:2000, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
    <div style={{ background:"#141414", border:"1px solid #2e2e2e", maxWidth:500, width:"100%", padding:"32px", boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>

      <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds — Simulated Email</div>
      <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:24 }}>Account Confirmed</div>

      {/* Email mockup */}
      <div style={{ background:"#0d0d0d", border:"1px solid #1f1f1f", padding:"20px 24px" }}>
        <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginBottom:2 }}>
          <b style={{ color:"#bbb" }}>To:</b> {member.email}
        </div>
        <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginBottom:16 }}>
          <b style={{ color:"#bbb" }}>Subject:</b> Welcome to OPSWAT Playgrounds — you're in.
        </div>
        <div style={{ borderTop:"1px solid #1f1f1f", paddingTop:16, fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.8 }}>
          <p style={{ marginBottom:12 }}>Hey <b style={{ color:"#fff" }}>{member.name}</b>,</p>
          <p style={{ marginBottom:12 }}>Your account has been set up and you're ready to go.</p>
          <div style={{ background:"#141414", border:"1px solid #1f1f1f", padding:"14px 16px", marginBottom:16 }}>
            <div style={{ fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>Your Account Details</div>
            <div style={{ fontSize:12, color:"#bbb", lineHeight:2 }}>
              <span style={{ color:"#bbb" }}>Display Name:</span> <span style={{ color:"#fff" }}>{member.name}</span><br/>
              <span style={{ color:"#bbb" }}>Username:</span> <span style={{ color:"#fff" }}>@{member.username}</span><br/>
              <span style={{ color:"#bbb" }}>Email:</span> <span style={{ color:"#fff" }}>{member.email}</span><br/>
              <span style={{ color:"#bbb" }}>Role:</span> <span style={{ color:"#fff" }}>{member.role}</span><br/>
              <span style={{ color:"#bbb" }}>Access Level:</span> <span style={{ color:"#fff" }}>Admin ✓</span>
            </div>
          </div>
          <p style={{ marginBottom:16, color:"#bbb" }}>You can now post work, leave feedback, manage your profile, and upload a profile photo.</p>
          <div style={{ display:"inline-block", padding:"8px 18px", border:"1px solid #fff", color:"#fff", fontSize:11, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:800 }}>Go to Playgrounds →</div>
        </div>
      </div>

      <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end" }}>
        <Btn onClick={onClose} variant="primary">Got It →</Btn>
      </div>
    </div>
  </div>
);

// ─── Sign In modal ────────────────────────────────────────────────────────────

// Shared simulated-email shell used by both recovery flows
const SimEmailShell = ({ to, subject, children, onClose, onBack }) => (
  <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1100, background:"rgba(0,0,0,0.95)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
    <div style={{ background:"#141414", border:"1px solid #2e2e2e", width:"100%", maxWidth:460, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>
      {/* Email chrome */}
      <div style={{ background:"#0d0d0d", borderBottom:"1px solid #1f1f1f", padding:"14px 20px", display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:10, height:10, borderRadius:"50%", background:"#2e2e2e" }}/>
        <div style={{ width:10, height:10, borderRadius:"50%", background:"#2e2e2e" }}/>
        <div style={{ width:10, height:10, borderRadius:"50%", background:"#2e2e2e" }}/>
        <span style={{ fontSize:10, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginLeft:8 }}>Simulated Email Preview</span>
      </div>
      <div style={{ padding:"24px 28px" }}>
        <div style={{ fontSize:9, color:"#aaa", fontFamily:F.barlow, marginBottom:4 }}>To: <span style={{ color:"#bbb" }}>{to}</span></div>
        <div style={{ fontSize:9, color:"#aaa", fontFamily:F.barlow, marginBottom:16 }}>From: <span style={{ color:"#bbb" }}>noreply@opswat-playgrounds.io</span></div>
        <div style={{ fontSize:14, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:20, paddingBottom:16, borderBottom:"1px solid #1f1f1f" }}>{subject}</div>
        {children}
        <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid #1f1f1f", fontSize:10, color:"#aaa", fontFamily:F.barlow, lineHeight:1.6 }}>
          If you didn't request this, you can safely ignore this email.<br/>
          — OPSWAT Playgrounds Team
        </div>
      </div>
      <div style={{ padding:"0 28px 24px", display:"flex", gap:10 }}>
        <Btn onClick={onBack} variant="ghost" style={{ flex:1, fontSize:10, padding:"8px" }}>← Back to Sign In</Btn>
        <Btn onClick={onClose} variant="primary" style={{ flex:1, fontSize:10, padding:"8px" }}>Got It →</Btn>
      </div>
    </div>
  </div>
);

// Forgot Password — 3 sub-screens: enter email → email preview → reset form
const ForgotPasswordFlow = ({ members, onClose, onBack, onPasswordReset }) => {
  const [screen, setScreen]   = useState("enter");   // enter | sent | reset
  const [email, setEmail]     = useState("");
  const [error, setError]     = useState("");
  const [matched, setMatched] = useState(null);
  const [newPw, setNewPw]     = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [pwError, setPwError] = useState("");

  const handleSend = () => {
    const m = members.find(m => m.email?.toLowerCase() === email.trim().toLowerCase());
    if (!m) { setError("No account found with that email address."); return; }
    setMatched(m);
    setScreen("sent");
  };

  const handleReset = () => {
    if (newPw.length < 6) { setPwError("Password must be at least 6 characters."); return; }
    if (newPw !== confirmPw) { setPwError("Passwords don't match."); return; }
    onPasswordReset(matched.id, newPw);
  };

  if (screen === "sent") return (
    <SimEmailShell to={matched.email} subject="Reset Your Password" onClose={()=>setScreen("reset")} onBack={onBack}>
      <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.75, marginBottom:20 }}>
        Hi {matched.name.split(" ")[0]}, we received a request to reset your password for your OPSWAT Playgrounds account (<span style={{ color:"#fff" }}>@{matched.username}</span>).
      </p>
      <div style={{ background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"14px 18px", marginBottom:4 }}>
        <div style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, marginBottom:10 }}>Click the button below to choose a new password:</div>
        <Btn onClick={()=>setScreen("reset")} variant="primary" style={{ width:"100%", padding:"11px", fontSize:11 }}>Reset My Password →</Btn>
      </div>
      <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:10 }}>This link expires in 30 minutes.</div>
    </SimEmailShell>
  );

  if (screen === "reset") return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1100, background:"rgba(0,0,0,0.95)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:420, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>
        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
        <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>Set New Password</div>
        <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, marginBottom:24 }}>Resetting password for <span style={{ color:"#bbb" }}>@{matched.username}</span></div>

        <Section>
          <Label>New Password</Label>
          <div style={{ position:"relative" }}>
            <Input value={newPw} onChange={e=>{ setNewPw(e.target.value); setPwError(""); }} placeholder="Min. 6 characters" type={showPw?"text":"password"} />
            <button onClick={()=>setShowPw(v=>!v)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#bbb", fontSize:15, padding:0, lineHeight:1 }}>{showPw?"🙈":"👁"}</button>
          </div>
        </Section>
        <Section>
          <Label>Confirm Password</Label>
          <Input value={confirmPw} onChange={e=>{ setConfirmPw(e.target.value); setPwError(""); }} placeholder="Type it again" type={showPw?"text":"password"} />
        </Section>

        {pwError && <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", marginBottom:16 }}>{pwError}</div>}

        <Btn onClick={handleReset} variant="primary" style={{ width:"100%", padding:"13px", marginBottom:10 }}>Update Password →</Btn>
        <Btn onClick={onBack} variant="ghost" style={{ width:"100%", padding:"10px", fontSize:10 }}>← Back to Sign In</Btn>
      </div>
    </div>
  );

  // screen === "enter"
  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1100, background:"rgba(0,0,0,0.95)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:420, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>
        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
        <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>Forgot Password</div>
        <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.7, marginBottom:24 }}>Enter your registered email and we'll send you a reset link.</p>
        <Section>
          <Label>Registered Email</Label>
          <Input value={email} onChange={e=>{ setEmail(e.target.value); setError(""); }} onKeyDown={e=>{ if(e.key==="Enter") handleSend(); }} placeholder="you@opswat.com" />
        </Section>
        {error && <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", marginBottom:16 }}>{error}</div>}
        <Btn onClick={handleSend} variant="primary" style={{ width:"100%", padding:"13px", marginBottom:10 }}>Send Reset Link →</Btn>
        <Btn onClick={onBack} variant="ghost" style={{ width:"100%", padding:"10px", fontSize:10 }}>← Back to Sign In</Btn>
      </div>
    </div>
  );
};

// Forgot Username — enter email → email preview
const ForgotUsernameFlow = ({ members, onBack }) => {
  const [screen, setScreen]   = useState("enter");
  const [email, setEmail]     = useState("");
  const [error, setError]     = useState("");
  const [matched, setMatched] = useState(null);

  const handleSend = () => {
    const m = members.find(m => m.email?.toLowerCase() === email.trim().toLowerCase());
    if (!m) { setError("No account found with that email address."); return; }
    setMatched(m);
    setScreen("sent");
  };

  if (screen === "sent") return (
    <SimEmailShell to={matched.email} subject="Your Username" onClose={onBack} onBack={onBack}>
      <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.75, marginBottom:20 }}>
        Hi {matched.name.split(" ")[0]}, here's the username associated with this email address:
      </p>
      <div style={{ background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"18px 20px", marginBottom:4, textAlign:"center" }}>
        <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:8 }}>Your Username</div>
        <div style={{ fontSize:28, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em" }}>@{matched.username}</div>
      </div>
      <p style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, lineHeight:1.7, marginTop:14 }}>
        You can sign in using either your username or your email address (<span style={{ color:"#bbb" }}>{matched.email}</span>).
      </p>
    </SimEmailShell>
  );

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1100, background:"rgba(0,0,0,0.95)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:420, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>
        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
        <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>Forgot Username</div>
        <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.7, marginBottom:24 }}>Enter your registered email and we'll send your username to your inbox.</p>
        <Section>
          <Label>Registered Email</Label>
          <Input value={email} onChange={e=>{ setEmail(e.target.value); setError(""); }} onKeyDown={e=>{ if(e.key==="Enter") handleSend(); }} placeholder="you@opswat.com" />
        </Section>
        {error && <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", marginBottom:16 }}>{error}</div>}
        <Btn onClick={handleSend} variant="primary" style={{ width:"100%", padding:"13px", marginBottom:10 }}>Send My Username →</Btn>
        <Btn onClick={onBack} variant="ghost" style={{ width:"100%", padding:"10px", fontSize:10 }}>← Back to Sign In</Btn>
      </div>
    </div>
  );
};

const SignInModal = ({ onClose, onSignIn, onGoSignUp, members, onPasswordReset }) => {
  const [form, setForm]       = useState({ login:"", password:"" });
  const [error, setError]     = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [subFlow, setSubFlow] = useState(null); // null | "forgotPw" | "forgotUser"
  const set = (k,v) => { setForm(f=>({...f,[k]:v})); setError(""); };

  const handleSubmit = () => {
    const login = form.login.trim().toLowerCase();
    const match = members.find(m =>
      (m.email?.toLowerCase() === login || m.username?.toLowerCase() === login) &&
      m.password === form.password
    );
    if (match) { onSignIn(match); }
    else { setError("No account found with those credentials. Check your email/username and password."); }
  };

  const onKey = e => { if (e.key === "Enter") handleSubmit(); };

  if (subFlow === "forgotPw") return (
    <ForgotPasswordFlow members={members} onClose={onClose} onBack={()=>setSubFlow(null)}
      onPasswordReset={(id, pw) => { onPasswordReset(id, pw); setSubFlow(null); }} />
  );
  if (subFlow === "forgotUser") return (
    <ForgotUsernameFlow members={members} onBack={()=>setSubFlow(null)} />
  );

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1000, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:420, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
          <div>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
            <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>Sign In</div>
          </div>
          <button onClick={onClose} style={{ background:"#1f1f1f", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:16, width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        {/* Wrap in a fake form name to confuse browser autofill */}
        <div autoComplete="off">

        <Section>
          <Label>Email or Username</Label>
          <input
            name="opswat_login"
            autoComplete="off"
            value={form.login}
            onChange={e=>set("login",e.target.value)}
            onKeyDown={onKey}
            placeholder="you@opswat.com or username"
            style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" }}
          />
        </Section>

        <Section>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <Label>Password</Label>
            <button onClick={()=>setSubFlow("forgotPw")} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:10, fontFamily:F.barlow, textDecoration:"underline", padding:0 }}>Forgot password?</button>
          </div>
          <div style={{ position:"relative" }}>
            <input
              name="opswat_password"
              autoComplete="new-password"
              type={showPw ? "text" : "password"}
              value={form.password}
              onChange={e=>set("password",e.target.value)}
              onKeyDown={onKey}
              placeholder="Your password"
              style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 40px 10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" }}
            />
            <button onClick={()=>setShowPw(v=>!v)}
              style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", alignItems:"center", justifyContent:"center", color:"#bbb", fontSize:16, lineHeight:1 }}
              title={showPw?"Hide password":"Show password"}
            >
              {showPw
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </Section>

        </div>

        {error && (
          <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", marginBottom:16, lineHeight:1.5 }}>
            {error}
          </div>
        )}

        <Btn variant="primary" onClick={handleSubmit} style={{ width:"100%", padding:"13px", marginBottom:14 }}>
          Sign In →
        </Btn>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:"#bbb", fontFamily:F.barlow, marginBottom:0 }}>
          <span>Don't have an account?{" "}
            <button onClick={onGoSignUp} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:12, fontFamily:F.barlow, textDecoration:"underline", padding:0 }}>Create one</button>
          </span>
          <button onClick={()=>setSubFlow("forgotUser")} style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:11, fontFamily:F.barlow, textDecoration:"underline", padding:0, whiteSpace:"nowrap" }}>Forgot username?</button>
        </div>
      </div>
    </div>
  );
};

// ─── Sign Up modal ────────────────────────────────────────────────────────────

const SignUpModal = ({ onClose, onSignUp }) => {
  const [step, setStep] = useState(1); // 1=account, 2=profile
  const [form, setForm] = useState({ name:"", username:"", email:"", password:"", role:"Motion Designer", bio:"", behance:"", linkedin:"" });
  const [avatarPreview, setAvatarPreview] = useState("");
  const fileRef = useRef();
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const ROLES = [
    "Motion Designer","Senior Motion Designer","3D Artist","VFX Artist","Video Editor",
    "Web Designer","Product Designer","UI/UX Designer","Graphic Designer","Brand Designer",
    "Illustrator","Art Director","Creative Director",
  ];

  const handleAvatar = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const ready1 = form.name.trim() && form.username.trim() && form.email.trim() && form.password.length >= 6;

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1000, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20, overflowY:"auto" }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", borderRadius:2, padding:"36px 32px", width:"100%", maxWidth:500, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
          <div>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
            <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>
              {step===1 ? "Create Account" : "Build Your Profile"}
            </div>
          </div>
          <button onClick={onClose} style={{ background:"#1f1f1f", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:16, width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        {/* Step indicator */}
        <div style={{ display:"flex", gap:8, marginBottom:28 }}>
          {[1,2].map(n => (
            <div key={n} style={{ flex:1, height:2, background: n<=step ? "#fff" : "#2e2e2e", transition:"background 0.3s" }} />
          ))}
        </div>

        {step === 1 ? (
          <>
            <Section><Label>Full Name</Label><Input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Your full name" /></Section>
            <Section><Label>Username</Label><Input value={form.username} onChange={e=>set("username",e.target.value.replace(/\s/g,""))} placeholder="e.g. lamarj — no spaces" /></Section>
            <Section><Label>Email</Label><Input value={form.email} onChange={e=>set("email",e.target.value)} placeholder="you@opswat.com" type="email" /></Section>
            <Section><Label>Password</Label><Input value={form.password} onChange={e=>set("password",e.target.value)} placeholder="Min. 6 characters" type="password" /></Section>
            <Section>
              <Label>Role</Label>
              <select value={form.role} onChange={e=>set("role",e.target.value)} style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none" }}>
                {ROLES.map(r=><option key={r}>{r}</option>)}
              </select>
            </Section>
            <Btn variant="primary" onClick={()=>ready1&&setStep(2)} style={{ width:"100%", padding:"13px", opacity:ready1?1:0.4 }}>
              Continue →
            </Btn>
          </>
        ) : (
          <>
            {/* Avatar upload */}
            <Section>
              <Label>Profile Photo</Label>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ width:64, height:64, borderRadius:"50%", background:"#0d0d0d", border:"1px solid #2e2e2e", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {avatarPreview
                    ? <img src={avatarPreview} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : <span style={{ fontSize:24, color:"#aaa" }}>◈</span>
                  }
                </div>
                <div>
                  <Btn onClick={()=>fileRef.current.click()} variant="ghost" style={{ padding:"7px 14px" }}>Upload Photo</Btn>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} style={{ display:"none" }} />
                  <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:6 }}>JPG or PNG, max 2MB</div>
                </div>
              </div>
            </Section>
            <Section><Label>Bio</Label><Textarea value={form.bio} onChange={e=>set("bio",e.target.value)} placeholder="What do you do? What tools do you use?" rows={3} /></Section>
            <Section><Label>Behance URL</Label><Input value={form.behance} onChange={e=>set("behance",e.target.value)} placeholder="https://behance.net/yourname" /></Section>
            <Section><Label>LinkedIn URL</Label><Input value={form.linkedin} onChange={e=>set("linkedin",e.target.value)} placeholder="https://linkedin.com/in/yourname" /></Section>

            <div style={{ display:"flex", gap:10, marginTop:8 }}>
              <Btn onClick={()=>setStep(1)} variant="ghost" style={{ flex:1 }}>← Back</Btn>
              <Btn onClick={()=>onSignUp({ ...form, avatarUrl:avatarPreview })} variant="primary" style={{ flex:2 }}>Create Account →</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Cover Image Prompt (used inside site/figma/link/pdf tabs) ───────────────

const CoverImagePrompt = ({ coverImage, setCoverImage, covRef }) => (
  <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid #1f1f1f" }}>
    {/* Header row */}
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:9, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700 }}>Cover Image</span>
        {!coverImage && <span style={{ fontSize:9, background:"#e05555", color:"#fff", fontFamily:"'Barlow',sans-serif", fontWeight:700, padding:"2px 8px", letterSpacing:"0.06em", textTransform:"uppercase" }}>Required for card thumbnail</span>}
        {coverImage  && <span style={{ fontSize:9, background:"#1a4a1a", color:"#6dca6d", fontFamily:"'Barlow',sans-serif", fontWeight:700, padding:"2px 8px", letterSpacing:"0.06em", textTransform:"uppercase" }}>✓ Cover set</span>}
      </div>
      {coverImage && (
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <button onClick={()=>covRef.current.click()} style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase", padding:"4px 12px", transition:"all 0.15s" }} onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }} onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}>Change Cover ↺</button>
          <button onClick={()=>setCoverImage(null)} style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:11, fontFamily:"'Barlow',sans-serif", textDecoration:"underline", padding:0 }}>Remove</button>
        </div>
      )}
    </div>

    {coverImage ? (
      /* Preview of uploaded cover */
      <div style={{ position:"relative", border:"1px solid #2e2e2e", overflow:"hidden" }}>
        <img src={coverImage.src} alt="cover" style={{ width:"100%", height:180, objectFit:"cover", display:"block" }} />
        <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(transparent, rgba(0,0,0,0.7))", padding:"20px 14px 10px", display:"flex", alignItems:"flex-end", justifyContent:"space-between" }}>
          <span style={{ fontSize:9, color:"rgba(255,255,255,0.6)", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.12em", textTransform:"uppercase" }}>This is your project's cover photo</span>
          <button onClick={()=>covRef.current.click()} style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", color:"#fff", cursor:"pointer", fontSize:9, fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase", padding:"4px 10px" }}>Change ↺</button>
        </div>
      </div>
    ) : (
      /* Upload drop zone */
      <div onClick={()=>covRef.current.click()}
        style={{ background:"#0a0a0a", border:"2px dashed #e05555", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10, cursor:"pointer", padding:"28px 16px", transition:"all 0.15s" }}
        onMouseEnter={e=>{ e.currentTarget.style.borderColor="#ff7070"; e.currentTarget.style.background="#0f0808"; }}
        onMouseLeave={e=>{ e.currentTarget.style.borderColor="#e05555"; e.currentTarget.style.background="#0a0a0a"; }}
      >
        <div style={{ width:44, height:44, borderRadius:"50%", background:"rgba(224,85,85,0.12)", border:"1px solid rgba(224,85,85,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <span style={{ fontSize:20, color:"#e05555" }}>↑</span>
        </div>
        <div>
          <div style={{ fontSize:12, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:700, textAlign:"center", marginBottom:4 }}>Upload a Cover Image</div>
          <div style={{ fontSize:11, color:"#aaa", fontFamily:"'Barlow',sans-serif", textAlign:"center", lineHeight:1.5 }}>This is the first thing people see on your project card.<br/>JPG or PNG recommended.</div>
        </div>
        <div style={{ fontSize:9, color:"#e05555", fontFamily:"'Barlow',sans-serif", fontWeight:600 }}>Skip this and we'll auto-generate a title card instead</div>
      </div>
    )}
  </div>
);

// ─── Shared: Media Upload Panel (used by PostModal + EditProjectModal) ────────

const MEDIA_TABS_BY_KIND = {
  motion:  [
    { id:"images",  label:"Images",       hint:"Upload JPG or PNG — up to 10" },
    { id:"videos",  label:"Videos",       hint:"Upload MP4 or MOV — max 6" },
    { id:"gif",     label:"GIF",          hint:"Animated GIF — max 3 files, 15MB each" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF presentation or deck" },
    { id:"link",    label:"Video Link",   hint:"Vimeo or YouTube embed URL" },
    { id:"figma",   label:"Figma",        hint:"Figma prototype or file link" },
  ],
  web:     [
    { id:"cover",   label:"Cover Photo",  hint:"Upload a cover image for your project" },
    { id:"images",  label:"Screenshots",  hint:"Upload JPG or PNG — up to 10" },
    { id:"videos",  label:"Videos",       hint:"Upload MP4 or MOV — max 6" },
    { id:"figma",   label:"Figma",        hint:"Figma prototype or file link" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF presentation or deck" },
  ],
  ui:      [
    { id:"cover",   label:"Cover Photo",  hint:"Upload a cover image for your project" },
    { id:"figma",   label:"Figma",        hint:"Figma prototype or file link" },
    { id:"images",  label:"Screenshots",  hint:"Upload JPG or PNG — up to 10" },
    { id:"videos",  label:"Videos",       hint:"Upload MP4 or MOV — max 6" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF presentation or deck" },
  ],
  graphic: [
    { id:"images",  label:"Images",       hint:"Upload JPG or PNG — up to 10" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF brand deck or presentation" },
    { id:"figma",   label:"Figma",        hint:"Figma file or prototype link" },
  ],
  brand:   [
    { id:"images",  label:"Images",       hint:"Upload JPG or PNG — up to 10" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF brand deck or presentation" },
    { id:"figma",   label:"Figma",        hint:"Figma file or prototype link" },
    { id:"link",    label:"Video Link",   hint:"Vimeo or YouTube — brand reels, motion identity" },
  ],
  illustration: [
    { id:"images",  label:"Images",       hint:"Upload JPG or PNG — up to 10" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF or portfolio deck" },
    { id:"figma",   label:"Figma",        hint:"Figma file or prototype link" },
    { id:"link",    label:"Video Link",   hint:"Vimeo or YouTube — process video or timelapse" },
  ],
  tutorial: [
    { id:"link",    label:"Video Link",   hint:"Paste your Vimeo or YouTube tutorial link" },
    { id:"videos",  label:"Video Upload", hint:"Upload your tutorial video — max 6 files, 50MB each" },
    { id:"images",  label:"Images",       hint:"Upload screenshots or slides — up to 10" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF handout or reference sheet" },
  ],
  experiment: [
    { id:"cover",   label:"Cover Photo",  hint:"Upload a cover image for your project" },
    { id:"images",  label:"Images",       hint:"Upload JPG or PNG — up to 10" },
    { id:"videos",  label:"Videos",       hint:"Upload MP4 or MOV — max 6" },
    { id:"gif",     label:"GIF",          hint:"Animated GIF — max 3 files, 15MB each" },
    { id:"pdf",     label:"PDF / Deck",   hint:"Upload a PDF presentation or deck" },
    { id:"link",    label:"Video Link",   hint:"Vimeo or YouTube embed URL" },
    { id:"figma",   label:"Figma",        hint:"Figma prototype or file link" },
  ],
};

const MediaUploadPanel = ({ catKind, mediaTab, setMediaTab, images, setImages, videos, setVideos, gifs, setGifs, pdfs, setPdfs, mediaUrl, setMediaUrl, figmaUrl, setFigmaUrl, videoUrl, setVideoUrl, websiteUrl, setWebsiteUrl, coverImage, setCoverImage }) => {
  const imgRef = useRef();
  const vidRef = useRef();
  const gifRef = useRef();
  const pdfRef = useRef();
  const covRef = useRef();
  const tabs = MEDIA_TABS_BY_KIND[catKind] || MEDIA_TABS_BY_KIND.motion;
  const [uploadError, setUploadError] = useState("");

  // Tabs that need a cover image instead of inline images
  const COVER_TABS = ["cover", "site", "figma", "link", "pdf"];

  const IMAGE_MAX = 10;
  const VIDEO_MAX_BYTES = 50 * 1024 * 1024; // 50 MB
  const GIF_MAX = 3;
  const GIF_MAX_BYTES = 15 * 1024 * 1024; // 15 MB

  const clearError = () => setUploadError("");

  const addCover = e => {
    const file = e.target.files[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = ev => setCoverImage({ src: ev.target.result, name: file.name });
    r.readAsDataURL(file);
    e.target.value = "";
  };

  const addGifs = e => {
    const files = Array.from(e.target.files);
    const remaining = GIF_MAX - gifs.length;
    if (remaining <= 0) {
      setUploadError(`GIF limit reached — max ${GIF_MAX} GIFs per project.`);
      e.target.value = "";
      return;
    }
    const accepted = files.slice(0, remaining);
    const oversized = accepted.filter(f => f.size > GIF_MAX_BYTES);
    const valid     = accepted.filter(f => f.size <= GIF_MAX_BYTES);
    if (oversized.length > 0) {
      setUploadError(`${oversized.map(f=>f.name).join(", ")} ${oversized.length===1?"exceeds":"exceed"} the 15MB limit and ${oversized.length===1?"was":"were"} not added.`);
    } else clearError();
    valid.forEach(file => {
      const r = new FileReader();
      r.onload = ev => setGifs(prev => [...prev, { id: Date.now()+Math.random(), src: ev.target.result, name: file.name }]);
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const addImages = e => {
    const files = Array.from(e.target.files);
    const remaining = IMAGE_MAX - images.length;
    if (remaining <= 0) {
      setUploadError(`Image limit reached — max ${IMAGE_MAX} images per project.`);
      e.target.value = "";
      return;
    }
    const accepted = files.slice(0, remaining);
    const skipped  = files.length - accepted.length;
    if (skipped > 0) setUploadError(`Only ${accepted.length} of ${files.length} images added — max ${IMAGE_MAX} per project.`);
    else clearError();
    accepted.forEach((file, idx) => {
      const uniqueId = `img-${Date.now()}-${idx}-${Math.random()}`;
      const r = new FileReader();
      r.onload = ev => {
        const result = ev.target.result;
        setImages(prev => {
          // Prevent duplicate by checking if src already exists
          if (prev.some(img => img.src === result)) return prev;
          return [...prev, { id: uniqueId, src: result, name: file.name }];
        });
      };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const addVideos = e => {
    const files = Array.from(e.target.files).slice(0, 6 - videos.length);
    const oversized = files.filter(f => f.size > VIDEO_MAX_BYTES);
    const accepted  = files.filter(f => f.size <= VIDEO_MAX_BYTES);
    if (oversized.length > 0) {
      setUploadError(`${oversized.map(f=>f.name).join(", ")} ${oversized.length===1?"exceeds":"exceed"} the 50 MB limit and ${oversized.length===1?"was":"were"} not added.`);
    } else {
      clearError();
    }
    accepted.forEach((file, idx) => {
      const uniqueId = `vid-${Date.now()}-${idx}-${Math.random()}`;
      const r = new FileReader();
      r.onload = ev => {
        const result = ev.target.result;
        setVideos(prev => {
          if (prev.some(v => v.name === file.name && v.src === result)) return prev;
          return [...prev, { id: uniqueId, src: result, name: file.name }];
        });
      };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const addPdfs = async e => {
    const files = Array.from(e.target.files);
    clearError();

    // Load pdf.js from CDN if not already loaded
    if (!window.pdfjsLib) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    for (const file of files) {
      const reader = new FileReader();
      reader.onload = async ev => {
        const src  = ev.target.result;
        const id   = Date.now() + Math.random();
        let thumb  = null;

        try {
          // Convert base64 data URL to Uint8Array for pdf.js
          const base64 = src.split(",")[1];
          const binary = atob(base64);
          const bytes  = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

          const pdf  = await window.pdfjsLib.getDocument({ data: bytes }).promise;
          const page = await pdf.getPage(1);

          // Render at 1.5x scale for a crisp thumbnail
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas   = document.createElement("canvas");
          canvas.width   = viewport.width;
          canvas.height  = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          thumb = canvas.toDataURL("image/jpeg", 0.85);
        } catch (err) {
          // If thumbnail generation fails, proceed without it
          console.warn("PDF thumbnail failed:", err);
        }

        setPdfs(prev => [...prev, { id, name: file.name, src, thumb }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const inputStyle = { width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" };

  const TabBtn = ({ t }) => {
    const isCoverTab = t.id === "cover";
    const needsCoverWarning = isCoverTab && !coverImage;
    const isActive = mediaTab === t.id;

    // Background: white when active (red if cover tab with no cover), transparent otherwise
    const bg = isActive ? (needsCoverWarning ? "#e05555" : "#fff") : "transparent";
    // Border: white when active, red if cover warning, dark otherwise
    const border = needsCoverWarning ? "#e05555" : isActive ? "#fff" : "#2e2e2e";
    // Text: black on white active button, white on red active button, red if warning not active, normal otherwise
    const color = isActive ? (needsCoverWarning ? "#fff" : "#000") : needsCoverWarning ? "#e05555" : "#bbb";

    return (
      <button onClick={()=>setMediaTab(t.id)}
        style={{ padding:"5px 13px", border:"1px solid", borderColor:border, background:bg, color, fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s", position:"relative" }}>
        {t.label}
        {needsCoverWarning && !isActive && <span style={{ position:"absolute", top:-4, right:-4, width:8, height:8, borderRadius:"50%", background:"#e05555", display:"block" }} />}
      </button>
    );
  };

  const DropZone = ({ label, hint, badge, disabled, onClick, children }) => (
    <div>
      <div onClick={disabled ? undefined : e=>{ e.stopPropagation(); onClick(); }}
        style={{ minHeight:100, background:"#0d0d0d", border:`1px dashed ${disabled?"#1a1a1a":"#2e2e2e"}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8, cursor:disabled?"not-allowed":"pointer", transition:"border-color 0.15s", padding:16, opacity:disabled?0.4:1 }}
        onMouseEnter={e=>{ if(!disabled) e.currentTarget.style.borderColor="#bbb"; }}
        onMouseLeave={e=>{ if(!disabled) e.currentTarget.style.borderColor="#2e2e2e"; }}
      >
        {children || (<>
          <div style={{ fontSize:24, color:"#aaa" }}>↑</div>
          <div style={{ fontSize:11, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", textAlign:"center" }}>{label}</div>
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow }}>{hint}</div>
          {badge && <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"2px 8px" }}>{badge}</div>}
        </>)}
      </div>
    </div>
  );

  return (
    <div>
      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
        {tabs.map(t => <TabBtn key={t.id} t={t} />)}
      </div>

      {/* Cover reminder — shown when on a non-cover tab and no cover uploaded yet */}
      {tabs.some(t=>t.id==="cover") && !coverImage && mediaTab !== "cover" && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(224,85,85,0.08)", border:"1px solid rgba(224,85,85,0.3)", padding:"8px 14px", marginBottom:12, gap:10 }}>
          <span style={{ fontSize:11, color:"#e05555", fontFamily:F.barlow, lineHeight:1.4 }}>⚠ No cover photo set — your project card will show a blank thumbnail.</span>
          <button onClick={()=>setMediaTab("cover")} style={{ background:"none", border:"1px solid #e05555", color:"#e05555", cursor:"pointer", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", padding:"4px 12px", whiteSpace:"nowrap", flexShrink:0 }}>Add Cover ↗</button>
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={imgRef} type="file" accept="image/*" multiple onChange={addImages} style={{ display:"none" }} />
      <input ref={vidRef} type="file" accept="video/*" multiple onChange={addVideos} style={{ display:"none" }} />
      <input ref={gifRef} type="file" accept="image/gif" multiple onChange={addGifs} style={{ display:"none" }} />
      <input ref={pdfRef} type="file" accept="application/pdf" multiple onChange={addPdfs} style={{ display:"none" }} />
      <input ref={covRef} type="file" accept="image/*" onChange={addCover} style={{ display:"none" }} />

      {/* Error banner */}
      {uploadError && (
        <div style={{ display:"flex", alignItems:"center", gap:10, background:"#0d0d0d", border:"1px solid #555", padding:"10px 14px", marginBottom:10 }}>
          <span style={{ color:"#bbb", fontSize:13 }}>⚠</span>
          <span style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, flex:1, lineHeight:1.5 }}>{uploadError}</span>
          <button onClick={clearError} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:14, padding:"0 4px" }}>×</button>
        </div>
      )}

      {/* Cover Photo tab — web category first tab */}
      {mediaTab === "cover" && (
        <div>
          <div style={{ fontSize:11, color:"#aaa", fontFamily:F.barlow, lineHeight:1.6, marginBottom:14 }}>
            Upload a cover photo for your project. This is the hero image people see on the feed card and project page — make it count. A great cover is a screenshot of the homepage, a mockup, or a styled promo image.
          </div>
          <CoverImagePrompt coverImage={coverImage} setCoverImage={setCoverImage} covRef={covRef} />
        </div>
      )}

      {/* Images tab */}
      {mediaTab === "images" && (
        <div>
          <DropZone
            label={images.length >= IMAGE_MAX ? `Max ${IMAGE_MAX} ${catKind === "web" || catKind === "ui" ? "screenshots" : "images"} reached` : `Click to add ${catKind === "web" || catKind === "ui" ? "screenshots" : "images"}`}
            hint={images.length >= IMAGE_MAX ? `Remove one to add more` : `JPG or PNG — up to ${IMAGE_MAX} per project`}
            badge={`${images.length} / ${IMAGE_MAX}`}
            disabled={images.length >= IMAGE_MAX}
            onClick={()=>imgRef.current.click()}
          />
          {images.length > 0 && (
            <div style={{ marginTop:10 }}>
              {images.length > 1 && <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>Drag to reorder</div>}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(100px,1fr))", gap:6 }}>
                {images.map((img,i) => (
                  <div key={img.id} draggable
                    onDragStart={e=>{ e.dataTransfer.setData("imgId", img.id); }}
                    onDragOver={e=>e.preventDefault()}
                    onDrop={e=>{
                      e.preventDefault();
                      const dragId = e.dataTransfer.getData("imgId");
                      if (dragId === img.id) return;
                      setImages(prev => {
                        const arr = [...prev];
                        const fromIdx = arr.findIndex(x=>x.id===dragId);
                        const toIdx = arr.findIndex(x=>x.id===img.id);
                        const [moved] = arr.splice(fromIdx, 1);
                        arr.splice(toIdx, 0, moved);
                        return arr;
                      });
                    }}
                    style={{ position:"relative", paddingBottom:"100%", background:"#0d0d0d", overflow:"hidden", cursor:"grab" }}>
                    <img src={img.src} alt={img.name} style={{ position:"absolute", top:0, left:0, right:0, bottom:0, width:"100%", height:"100%", objectFit:"cover", pointerEvents:"none" }} />
                    <div style={{ position:"absolute", top:4, left:4, background:"rgba(0,0,0,0.7)", color:"#fff", fontSize:9, fontFamily:F.cond, width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center" }}>{i+1}</div>
                    <label style={{ position:"absolute", bottom:4, left:4, background:"rgba(0,0,0,0.8)", color:"#bbb", fontSize:8, fontFamily:F.cond, letterSpacing:"0.08em", textTransform:"uppercase", padding:"2px 6px", cursor:"pointer" }}>
                      Replace
                      <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{
                        const f = e.target.files[0]; if (!f) return;
                        const r = new FileReader();
                        r.onload = ev => setImages(prev => prev.map(x => x.id===img.id ? { ...x, src:ev.target.result, name:f.name } : x));
                        r.readAsDataURL(f);
                        e.target.value = "";
                      }} />
                    </label>
                    <button onClick={()=>{ setImages(prev=>prev.filter(x=>x.id!==img.id)); clearError(); }}
                      style={{ position:"absolute", top:4, right:4, background:"rgba(0,0,0,0.8)", border:"none", color:"#fff", width:20, height:20, borderRadius:"50%", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>x</button>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginTop:8 }}>
                {images.length} / {IMAGE_MAX} {catKind === "web" || catKind === "ui" ? "screenshots" : "images"}
                {images.length < IMAGE_MAX ? ` — ${IMAGE_MAX - images.length} slot${IMAGE_MAX - images.length !== 1 ? "s" : ""} remaining` : " — limit reached"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Videos tab */}
      {mediaTab === "videos" && (
        <div>
          <DropZone
            label="Click to add video"
            hint="MP4 or MOV · max 50 MB per file"
            badge={`${videos.length}/6 videos`}
            disabled={videos.length >= 6}
            onClick={()=>vidRef.current.click()}
          />
          {videos.length > 0 && (
            <div style={{ marginTop:10, display:"grid", gridTemplateColumns:videos.length===1?"1fr":"1fr 1fr", gap:8 }}>
              {videos.map(vid => (
                <div key={vid.id} style={{ position:"relative", background:"#000", border:"1px solid #1f1f1f" }}>
                  <video src={vid.src} controls style={{ width:"100%", display:"block", maxHeight:140 }} />
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 8px", borderTop:"1px solid #1f1f1f", gap:6 }}>
                    <span style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{vid.name}</span>
                    <label style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer", border:"1px solid #2e2e2e", padding:"2px 8px", flexShrink:0 }}>
                      Replace
                      <input type="file" accept="video/*" style={{ display:"none" }} onChange={e=>{
                        const f = e.target.files[0]; if (!f) return;
                        if (f.size > 50*1024*1024) { setUploadError("File exceeds 50 MB limit."); return; }
                        const r = new FileReader();
                        r.onload = ev => setVideos(prev => prev.map(v => v.id===vid.id ? { ...v, src:ev.target.result, name:f.name } : v));
                        r.readAsDataURL(f);
                        e.target.value = "";
                      }} />
                    </label>
                    <button onClick={()=>{ setVideos(prev=>prev.filter(x=>x.id!==vid.id)); clearError(); }} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:12, padding:"0 4px", flexShrink:0 }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:8 }}>
            Max 6 videos · 50 MB per file.{videos.length < 6 ? ` ${6-videos.length} slot${6-videos.length!==1?"s":""} remaining.` : " Limit reached."}
          </div>
        </div>
      )}

      {/* GIF tab — motion / experiment categories only */}
      {mediaTab === "gif" && (
        <div>
          <DropZone
            label={gifs.length >= GIF_MAX ? `Max ${GIF_MAX} GIFs reached` : "Click to add animated GIF"}
            hint={gifs.length >= GIF_MAX ? "Remove one to add more" : "Animated GIF only · max 15 MB each · up to 3"}
            badge={`${gifs.length} / ${GIF_MAX}`}
            disabled={gifs.length >= GIF_MAX}
            onClick={()=>gifRef.current.click()}
          />
          {gifs.length > 0 && (
            <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:8 }}>
              {gifs.map(gif => (
                <div key={gif.id} style={{ position:"relative", background:"#0d0d0d", border:"1px solid #1f1f1f" }}>
                  <img src={gif.src} alt={gif.name} style={{ width:"100%", display:"block", maxHeight:160, objectFit:"cover" }} />
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 8px", borderTop:"1px solid #1f1f1f" }}>
                    <span style={{ fontSize:9, color:"#bbb", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:100 }}>{gif.name}</span>
                    <button onClick={()=>{ setGifs(prev=>prev.filter(x=>x.id!==gif.id)); clearError(); }} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:12, padding:"0 4px" }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:8 }}>
            GIFs play automatically on the project page. Max 3 · 15 MB each.
          </div>
        </div>
      )}

      {/* PDF tab */}
      {mediaTab === "pdf" && (
        <div>
          <DropZone label="Click to upload PDF" hint="Presentation, case study, or brand deck" onClick={()=>pdfRef.current.click()} />
          {pdfs.length > 0 && (
            <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:6 }}>
              {pdfs.map(pdf => (
                <div key={pdf.id} style={{ display:"flex", alignItems:"center", gap:10, background:"#0d0d0d", border:"1px solid #1f1f1f", padding:"10px 14px" }}>
                  <span style={{ fontSize:18, color:"#bbb" }}>⬚</span>
                  <span style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, flex:1 }}>{pdf.name}</span>
                  <button onClick={()=>setPdfs(prev=>prev.filter(x=>x.id!==pdf.id))} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:14 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Figma embed */}
      {mediaTab === "figma" && (
        <div>
          <div style={{ marginBottom:8 }}>
            <input value={figmaUrl} onChange={e=>setFigmaUrl(e.target.value)} placeholder="https://www.figma.com/proto/... or /file/..." style={inputStyle} />
          </div>
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginBottom:4 }}>Paste a Figma prototype or file link — only figma.com URLs will embed. Non-Figma links will show as a clickable card.</div>
        </div>
      )}

      {/* Vimeo / YouTube link */}
      {mediaTab === "link" && (
        <div>
          <div style={{ marginBottom:8 }}>
            <input value={videoUrl} onChange={e=>setVideoUrl(e.target.value)} placeholder="https://vimeo.com/... or https://youtube.com/watch?v=..." style={inputStyle} />
          </div>
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginBottom:4 }}>Only YouTube and Vimeo URLs will embed directly. Other links will show as a clickable card.</div>
          {videoUrl.trim() && (
            <div style={{ marginTop:8, padding:"10px 12px", background:"#0d0d0d", border:"1px solid #2e2e2e", borderLeft:"2px solid #f5a623" }}>
              <div style={{ fontSize:10, color:"#f5a623", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>Heads Up</div>
              <div style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, lineHeight:1.6 }}>If your video is private or password-protected, the site cannot pull a thumbnail. We recommend uploading a cover image or screenshot in the Images tab so your project looks great in the feed.</div>
            </div>
          )}
        </div>
      )}

      {catKind !== "tutorial" && <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid #1f1f1f" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
          <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>
            {catKind === "web" ? "Live Site URL" : catKind === "ui" ? "Live / Staging URL" : "Website Link"}
          </span>
          <span style={{ fontSize:9, color:"#aaa", fontFamily:F.barlow }}>
            {catKind === "web" ? "— paste your client or project URL here" : catKind === "ui" ? "— deployed prototype or staging link" : "— optional · visible to all viewers as a clickable button"}
          </span>
        </div>
        <input
          value={websiteUrl}
          onChange={e=>setWebsiteUrl(e.target.value)}
          placeholder={catKind === "web" ? "https://yourclientsite.com" : catKind === "ui" ? "https://staging.yourapp.com" : "https://yoursite.com or https://behance.net/..."}
          style={inputStyle}
        />
        <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:6 }}>
          {catKind === "web"
            ? "This becomes a \"Visit Site →\" button on your project. Upload screenshots above to show what the site looks like."
            : catKind === "ui"
            ? "This becomes a \"View Prototype →\" button on your project."
            : "This becomes a \"Visit Website →\" button on your project. Use it for any live URL — a client site, Behance page, Dribbble shot, or staging link."
          }
        </div>
      </div>}
    </div>
  );
};

// ─── Upload / Post modal ──────────────────────────────────────────────────────

const PostModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ title:"", category:"Motion Design", description:"", challenges:"", tags:"" });
  const [mediaTab,    setMediaTab]    = useState("images");
  const [mediaUrl,    setMediaUrl]    = useState("");
  const [figmaUrl,    setFigmaUrl]    = useState("");
  const [videoUrl,    setVideoUrl]    = useState("");
  const [websiteUrl,  setWebsiteUrl]  = useState("");
  const [images,      setImages]      = useState([]);
  const [videos,      setVideos]      = useState([]);
  const [gifs,        setGifs]        = useState([]);
  const [pdfs,        setPdfs]        = useState([]);
  const [coverImage,  setCoverImage]  = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const ready = form.title.trim() && form.description.trim();
  const catKind = CAT_TYPE[form.category] || "motion";
  const inputStyle = { width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" };

  const handleCategoryChange = (c) => {
    set("category", c);
    const newKind = CAT_TYPE[c] || "motion";
    const firstTab = (MEDIA_TABS_BY_KIND[newKind]||[])[0]?.id || "images";
    setMediaTab(firstTab);
  };

  const handleSubmit = () => {
    if (!ready) return;
    onSubmit({
      title: form.title.trim(), category: form.category,
      description: form.description.trim(), challenges: form.challenges.trim(),
      tags: form.tags,
      difficulty: form.difficulty || "",
      duration: form.duration || "",
      mediaImages: images, mediaVideos: videos, mediaGifs: gifs, mediaPdfs: pdfs,
      mediaTab, mediaUrl: mediaUrl.trim(),
      figmaUrl: figmaUrl.trim(),
      videoUrl: videoUrl.trim(),
      websiteUrl: websiteUrl.trim(),
      coverImage: coverImage || null,
      mediaPreview: coverImage?.src || images[0]?.src || videos[0]?.src || videoThumb(videoUrl.trim()) || pdfs[0]?.thumb || "",
      mediaType: images.length ? "image" : videos.length ? "video" : mediaTab,
    });
  };

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1000, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20, overflowY:"auto" }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:600, boxShadow:"0 24px 80px rgba(0,0,0,0.9)", margin:"auto" }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
          <div>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
            <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>Post Your Work</div>
          </div>
          <button onClick={onClose} style={{ background:"#1f1f1f", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:16, width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        <Section><Label>Project Title *</Label><input value={form.title} onChange={e=>set("title",e.target.value)} placeholder="e.g. Brand Refresh — Homepage V2" style={inputStyle} /></Section>

        <Section>
          <Label>Category</Label>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>Motion & Video</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
              {["Motion Design","3D Render","VFX","Video Editing"].map(c=>(
                <button key={c} onClick={()=>handleCategoryChange(c)} style={{ padding:"5px 12px", border:"1px solid", borderColor:form.category===c?"#fff":"#2e2e2e", background:form.category===c?"#fff":"transparent", color:form.category===c?"#000":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s" }}>{c}</button>
              ))}
            </div>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>Design</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
              {["Web Design","Product / UI Design","Graphic Design","Brand & Identity","Illustration"].map(c=>(
                <button key={c} onClick={()=>handleCategoryChange(c)} style={{ padding:"5px 12px", border:"1px solid", borderColor:form.category===c?"#fff":"#2e2e2e", background:form.category===c?"#fff":"transparent", color:form.category===c?"#000":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s" }}>{c}</button>
              ))}
            </div>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>Other</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {["Experiment","Tutorial"].map(c=>(
                <button key={c} onClick={()=>handleCategoryChange(c)} style={{ padding:"5px 12px", border:"1px solid", borderColor:form.category===c?"#fff":"#2e2e2e", background:form.category===c?"#fff":"transparent", color:form.category===c?"#000":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s" }}>{c}</button>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <Label>Project Description *</Label>
          <textarea value={form.description} onChange={e=>set("description",e.target.value)} placeholder="What is this project? What are you exploring or building?" rows={3} style={{ ...inputStyle, resize:"vertical" }} />
        </Section>

        {form.category !== "Tutorial" && (
          <Section>
            <Label>Challenges</Label>
            <textarea value={form.challenges} onChange={e=>set("challenges",e.target.value)} placeholder="What problems are you running into? What's blocking you? This gets shared when you request feedback." rows={3} style={{ ...inputStyle, resize:"vertical" }} />
          </Section>
        )}

        <Section><Label>Tags</Label><input value={form.tags} onChange={e=>set("tags",e.target.value)} placeholder="e.g. Figma, Illustrator, WIP, Branding..." style={inputStyle} /></Section>

        {form.category === "Tutorial" && (
          <div style={{ display:"flex", gap:12 }}>
            <Section style={{ flex:1 }}>
              <Label>Difficulty</Label>
              <select value={form.difficulty||"Beginner"} onChange={e=>set("difficulty",e.target.value)} style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none" }}>
                {["Beginner","Intermediate","Advanced"].map(d=><option key={d}>{d}</option>)}
              </select>
            </Section>
            <Section style={{ flex:1 }}>
              <Label>Duration</Label>
              <input value={form.duration||""} onChange={e=>set("duration",e.target.value)} placeholder="e.g. 30 min, 1 hr" style={inputStyle} />
            </Section>
          </div>
        )}

        <Section>
          <Label>Project Media</Label>
          <MediaUploadPanel
            catKind={catKind}
            mediaTab={mediaTab}        setMediaTab={setMediaTab}
            images={images}            setImages={setImages}
            videos={videos}            setVideos={setVideos}
            gifs={gifs}                setGifs={setGifs}
            pdfs={pdfs}                setPdfs={setPdfs}
            mediaUrl={mediaUrl}        setMediaUrl={setMediaUrl}
            figmaUrl={figmaUrl}        setFigmaUrl={setFigmaUrl}
            videoUrl={videoUrl}        setVideoUrl={setVideoUrl}
            websiteUrl={websiteUrl}    setWebsiteUrl={setWebsiteUrl}
            coverImage={coverImage}    setCoverImage={setCoverImage}
          />
        </Section>

        <Divider />

        <div style={{ background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"14px 18px", marginBottom:16, display:"flex", alignItems:"center", gap:14 }}>
          <span style={{ fontSize:20, color:"#bbb" }}>{CAT_ICON[form.category]||"◎"}</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:3 }}>Posting to Category</div>
            <div style={{ fontSize:14, fontWeight:800, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>{form.category}</div>
          </div>
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow }}>Wrong?<br/>Select above ↑</div>
        </div>

        <Btn variant="primary" onClick={handleSubmit} style={{ width:"100%", padding:"13px", opacity:ready?1:0.4 }}>
          Publish to Playgrounds →
        </Btn>
        {!ready && <div style={{ fontSize:11, color:"#aaa", fontFamily:F.barlow, textAlign:"center", marginTop:10 }}>Title and description are required</div>}
      </div>
    </div>
  );
};

// ─── Edit Project Modal ────────────────────────────────────────────────────────

const EditProjectModal = ({ project, onClose, onSave }) => {
  const CAT_TYPE_LOCAL = {
    "Motion Design":"motion","3D Render":"motion","VFX":"motion","Video Editing":"motion","Experiment":"experiment","Tutorial":"tutorial",
    "Web Design":"web","Product / UI Design":"ui",
    "Graphic Design":"graphic","Brand & Identity":"brand","Illustration":"illustration",
  };

  const [form, setForm] = useState({
    title:       project.title       || "",
    category:    project.category    || "Motion Design",
    description: project.description || "",
    challenges:  project.challenges  || "",
    tags:        (project.tags || []).join(", "),
  });
  const [mediaTab,    setMediaTab]    = useState(project.mediaTab  || "images");
  const [mediaUrl,    setMediaUrl]    = useState(project.mediaUrl  || "");
  const [figmaUrl,    setFigmaUrl]    = useState(project.figmaUrl  || "");
  const [videoUrl,    setVideoUrl]    = useState(project.videoUrl  || "");
  const [websiteUrl,  setWebsiteUrl]  = useState(project.websiteUrl || "");
  const [coverImage,  setCoverImage]  = useState(project.coverImage || null);
  const [images,    setImages]    = useState(project.mediaImages || (project.mediaPreview && project.mediaPreview.startsWith("data:image") ? [{ id:"legacy", src:project.mediaPreview, name:"image" }] : []));
  const [videos,    setVideos]    = useState(project.mediaVideos || (project.mediaPreview && project.mediaPreview.startsWith("data:video") ? [{ id:"legacy", src:project.mediaPreview, name:"video" }] : []));
  const [gifs,      setGifs]      = useState(project.mediaGifs   || []);
  const [pdfs,      setPdfs]      = useState(project.mediaPdfs  || (project.mediaPreview && project.mediaPreview.startsWith("pdf:") ? [{ id:"legacy", name:project.mediaPreview.replace("pdf:","") }] : []));
  const [saved, setSaved] = useState(false);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const catKind = CAT_TYPE_LOCAL[form.category] || "motion";
  const ready   = form.title.trim() && form.description.trim();
  const inputStyle = { width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" };

  const handleCategoryChange = (c) => {
    set("category", c);
    const newKind  = CAT_TYPE_LOCAL[c] || "motion";
    const firstTab = (MEDIA_TABS_BY_KIND[newKind]||[])[0]?.id || "images";
    setMediaTab(firstTab);
  };

  const handleSave = () => {
    if (!ready) return;
    const updated = {
      ...project,
      title:        form.title.trim(),
      category:     form.category,
      description:  form.description.trim(),
      challenges:   form.challenges.trim(),
      tags:         form.tags.split(",").map(t=>t.trim()).filter(Boolean),
      mediaTab, mediaUrl: mediaUrl.trim(),
      figmaUrl:     figmaUrl.trim(),
      videoUrl:     videoUrl.trim(),
      websiteUrl:   websiteUrl.trim(),
      coverImage:   coverImage || null,
      mediaImages:  images,
      mediaVideos:  videos,
      mediaGifs:    gifs,
      mediaPdfs:    pdfs,
      mediaPreview: coverImage?.src || images[0]?.src || videos[0]?.src || videoThumb(videoUrl.trim()) || pdfs[0]?.thumb || "",
      mediaType:    images.length ? "image" : videos.length ? "video" : mediaTab,
    };
    onSave(updated);
    setSaved(true);
    setTimeout(() => onClose(), 800);
  };

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1100, background:"rgba(0,0,0,0.95)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20, overflowY:"auto" }}>
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:600, boxShadow:"0 24px 80px rgba(0,0,0,0.9)", margin:"auto" }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
          <div>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
            <div style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>Edit Project</div>
            <div style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, marginTop:4 }}>{project.title}</div>
          </div>
          <button onClick={onClose} style={{ background:"#1f1f1f", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:16, width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        <Section><Label>Project Title *</Label><input value={form.title} onChange={e=>set("title",e.target.value)} style={inputStyle} /></Section>

        <Section>
          <Label>Category</Label>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>Motion & Video</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
              {["Motion Design","3D Render","VFX","Video Editing"].map(c=>(
                <button key={c} onClick={()=>handleCategoryChange(c)} style={{ padding:"5px 12px", border:"1px solid", borderColor:form.category===c?"#fff":"#2e2e2e", background:form.category===c?"#fff":"transparent", color:form.category===c?"#000":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s" }}>{c}</button>
              ))}
            </div>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>Design</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
              {["Web Design","Product / UI Design","Graphic Design","Brand & Identity","Illustration"].map(c=>(
                <button key={c} onClick={()=>handleCategoryChange(c)} style={{ padding:"5px 12px", border:"1px solid", borderColor:form.category===c?"#fff":"#2e2e2e", background:form.category===c?"#fff":"transparent", color:form.category===c?"#000":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s" }}>{c}</button>
              ))}
            </div>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>Other</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {["Experiment","Tutorial"].map(c=>(
                <button key={c} onClick={()=>handleCategoryChange(c)} style={{ padding:"5px 12px", border:"1px solid", borderColor:form.category===c?"#fff":"#2e2e2e", background:form.category===c?"#fff":"transparent", color:form.category===c?"#000":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s" }}>{c}</button>
              ))}
            </div>
          </div>
        </Section>

        <Section><Label>Project Description *</Label><textarea value={form.description} onChange={e=>set("description",e.target.value)} rows={3} style={{ ...inputStyle, resize:"vertical" }} /></Section>
        <Section><Label>Challenges</Label><textarea value={form.challenges} onChange={e=>set("challenges",e.target.value)} rows={3} style={{ ...inputStyle, resize:"vertical" }} /></Section>
        <Section><Label>Tags</Label><input value={form.tags} onChange={e=>set("tags",e.target.value)} placeholder="e.g. Cinema 4D, Octane, WIP…" style={inputStyle} /></Section>

        <Section>
          <Label>Project Media</Label>
          <MediaUploadPanel
            catKind={catKind}
            mediaTab={mediaTab}        setMediaTab={setMediaTab}
            images={images}            setImages={setImages}
            videos={videos}            setVideos={setVideos}
            pdfs={pdfs}                setPdfs={setPdfs}
            gifs={gifs}                setGifs={setGifs}
            mediaUrl={mediaUrl}        setMediaUrl={setMediaUrl}
            figmaUrl={figmaUrl}        setFigmaUrl={setFigmaUrl}
            videoUrl={videoUrl}        setVideoUrl={setVideoUrl}
            websiteUrl={websiteUrl}    setWebsiteUrl={setWebsiteUrl}
            coverImage={coverImage}    setCoverImage={setCoverImage}
          />
        </Section>

        <Divider />

        <Btn variant="primary" onClick={handleSave}
          style={{ width:"100%", padding:"13px", opacity:ready?1:0.4, background:saved?"#666":"#fff", color:saved?"#fff":"#000", transition:"all 0.3s" }}>
          {saved ? "✓ Changes Saved" : "Save Changes →"}
        </Btn>
        {!ready && <div style={{ fontSize:11, color:"#aaa", fontFamily:F.barlow, textAlign:"center", marginTop:10 }}>Title and description are required</div>}
      </div>
    </div>
  );
};

// ─── Comment Row (with edit / delete) ────────────────────────────────────────

const COMMENT_MAX = 500;

const CommentRow = ({ comment, author, isOwn, onEdit, onDelete }) => {
  const [mode, setMode]         = useState("view");
  const [editText, setEditText] = useState(comment.text);

  const handleSave = () => {
    if (!editText.trim() || editText.length > COMMENT_MAX) return;
    onEdit(editText.trim());
    setMode("view");
  };

  return (
    <div style={{ display:"flex", gap:12, marginBottom:20, paddingBottom:20, borderBottom:"1px solid #1f1f1f" }}>
      <Avatar member={author} size={32} />
      <div style={{ flex:1, minWidth:0 }}>

        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6, flexWrap:"wrap" }}>
          <span style={{ fontSize:13, fontWeight:700, color:"#bbb", fontFamily:F.barlow }}>{author?.name}</span>
          <span style={{ fontSize:11, color:"#aaa", fontFamily:F.barlow }}>{comment.time}</span>
          {comment.edited && <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, fontStyle:"italic" }}>edited</span>}
          {isOwn && mode === "view" && (
            <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
              <button onClick={()=>{ setEditText(comment.text); setMode("edit"); }}
                style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"2px 8px", transition:"color 0.15s" }}
                onMouseEnter={e=>e.currentTarget.style.color="#bbb"}
                onMouseLeave={e=>e.currentTarget.style.color="#aaa"}
              >Edit</button>
              <button onClick={()=>setMode("confirmDelete")}
                style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"2px 8px", transition:"color 0.15s" }}
                onMouseEnter={e=>e.currentTarget.style.color="#bbb"}
                onMouseLeave={e=>e.currentTarget.style.color="#aaa"}
              >Delete</button>
            </div>
          )}
        </div>

        {mode === "view" && (
          <div style={{ fontSize:13, color:"#bbb", lineHeight:1.65, fontFamily:F.barlow }}>
            {comment.text.split(/(@\w+)/g).map((part, i) =>
              /^@\w+$/.test(part)
                ? <span key={i} style={{ color:"#fff", fontWeight:600 }}>{part}</span>
                : part
            )}
          </div>
        )}

        {mode === "edit" && (
          <div>
            <textarea value={editText} onChange={e=>setEditText(e.target.value)} autoFocus rows={3}
              style={{ width:"100%", background:"#0d0d0d", border:`1px solid ${editText.length>COMMENT_MAX?"#e05555":"#555"}`, padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", resize:"vertical", boxSizing:"border-box", marginBottom:4 }}
              onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); handleSave(); } if(e.key==="Escape") setMode("view"); }}
            />
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow }}>Enter to save · Esc to cancel · Shift+Enter for new line</span>
              <span style={{ fontSize:10, fontFamily:F.barlow, color:editText.length>COMMENT_MAX?"#e05555":editText.length>450?"#bbb":"#aaa" }}>
                {editText.length > COMMENT_MAX ? `${editText.length-COMMENT_MAX} over limit` : `${COMMENT_MAX-editText.length} remaining`}
              </span>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <Btn variant="primary" onClick={handleSave} style={{ padding:"6px 16px", fontSize:10, opacity:editText.length>COMMENT_MAX?0.4:1 }}>Save</Btn>
              <Btn variant="ghost"   onClick={()=>setMode("view")} style={{ padding:"6px 16px", fontSize:10 }}>Cancel</Btn>
            </div>
          </div>
        )}

        {mode === "confirmDelete" && (
          <div style={{ display:"flex", alignItems:"center", gap:10, background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 14px", marginTop:4 }}>
            <span style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, flex:1 }}>Delete this comment?</span>
            <button onClick={onDelete} style={{ background:"#fff", border:"none", color:"#000", cursor:"pointer", fontSize:10, fontFamily:F.cond, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 14px" }}>Yes, Delete</button>
            <button onClick={()=>setMode("view")} style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 14px" }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Project detail view ──────────────────────────────────────────────────────

const ProjectDetail = ({ project, getMember, allMembers, currentUser, onClose, onReact, onComment, onEditComment, onDeleteComment, onRequestFeedback, onDelete, onUpdate, onTagClick, showToast }) => {
  const [newComment, setNewComment] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const author = getMember(project.authorId);
  const totalReactions = Object.values(project.reactions).reduce((a,b)=>a+b,0);
  const userReactions = currentUser ? (project.userReactions?.[currentUser.id] || []) : [];
  const canDelete = currentUser && (currentUser.id === project.authorId || currentUser.isAdmin);
  const isOwner  = currentUser && currentUser.id === project.authorId;

  const embedUrl = (url) => {
    if (!url) return "";
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    // Figma — convert share URLs to embed URLs
    if (url.includes("figma.com")) {
      return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`;
    }
    // Live site or anything else — pass through
    return url;
  };

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:500, background:"#000", overflowY:"auto", padding:"40px 20px" }}>
      <div style={{ maxWidth:800, margin:"0 auto" }}>

        {/* Top bar */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28 }}>
          <Btn onClick={onClose} variant="ghost" style={{ padding:"7px 16px", fontSize:10 }}>← Back to Playgrounds</Btn>

          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {/* Share button */}
            <button onClick={()=>{
              const url = window.location.origin + "/?project=" + project.id;
              if (navigator.clipboard) {
                navigator.clipboard.writeText(url).then(()=>showToast("Link Copied", project.title.toUpperCase(), "Share this link to open the project directly."));
              } else { window.prompt("Copy this link:", url); }
            }} style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer", padding:"6px 14px" }}
              onMouseEnter={e=>e.currentTarget.style.borderColor="#fff"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="#2e2e2e"}>
              Share
            </button>
            {/* Edit — owner only */}
            {isOwner && !confirmDelete && (
              <button onClick={()=>setShowEdit(true)}
                style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", padding:"7px 14px", transition:"all 0.15s" }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
              >Edit Project</button>
            )}

            {/* Delete */}
            {canDelete && !confirmDelete && (
              <button onClick={()=>setConfirmDelete(true)}
                style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", padding:"7px 14px", transition:"all 0.15s" }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor="#bbb"; e.currentTarget.style.color="#bbb"; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
              >Delete Project</button>
            )}

            {confirmDelete && (
              <div style={{ display:"flex", alignItems:"center", gap:10, background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"8px 14px" }}>
                <span style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow }}>Remove this project permanently?</span>
                <button onClick={()=>onDelete(project.id)} style={{ background:"#fff", border:"none", color:"#000", fontSize:10, fontFamily:F.cond, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 14px", cursor:"pointer" }}>Yes, Delete</button>
                <button onClick={()=>setConfirmDelete(false)} style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 14px", cursor:"pointer" }}>Cancel</button>
              </div>
            )}
          </div>
        </div>

        {/* Edit modal */}
        {showEdit && (
          <EditProjectModal
            project={project}
            onClose={()=>setShowEdit(false)}
            onSave={(updated)=>{ onUpdate(updated); setShowEdit(false); }}
          />
        )}

        {/* Lightbox — image fullscreen with prev/next */}
        {lightbox !== false && (() => {
          const imgs = project.mediaImages?.length ? project.mediaImages : [];
          const idx   = typeof lightbox === "number" ? lightbox : 0;
          const img   = imgs[idx];
          const prev  = idx > 0 ? ()=>setLightbox(idx-1) : null;
          const next  = idx < imgs.length-1 ? ()=>setLightbox(idx+1) : null;
          return img ? (
            <div onClick={()=>setLightbox(false)}
              style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:2000, background:"rgba(0,0,0,0.97)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"zoom-out" }}>
              <img src={img.src} alt="" onClick={e=>e.stopPropagation()}
                style={{ maxWidth:"90vw", maxHeight:"90vh", objectFit:"contain", userSelect:"none" }} />
              {prev && <button onClick={e=>{e.stopPropagation();prev();}}
                style={{ position:"absolute", left:20, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.08)", border:"1px solid #555", color:"#fff", width:44, height:44, borderRadius:"50%", fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>}
              {next && <button onClick={e=>{e.stopPropagation();next();}}
                style={{ position:"absolute", right:20, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.08)", border:"1px solid #555", color:"#fff", width:44, height:44, borderRadius:"50%", fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>}
              <button onClick={()=>setLightbox(false)}
                style={{ position:"absolute", top:20, right:20, background:"rgba(255,255,255,0.08)", border:"1px solid #555", color:"#fff", width:40, height:40, borderRadius:"50%", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
              {imgs.length > 1 && <div style={{ position:"absolute", bottom:20, left:"50%", transform:"translateX(-50%)", fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>{idx+1} / {imgs.length} · Click outside to close</div>}
            </div>
          ) : null;
        })()}

        {/* ── Cover Image (web/ui/experiment projects with coverImage) ── */}
        {project.coverImage?.src && (
          <div style={{ marginBottom:4, position:"relative" }}>
            <img
              src={project.coverImage.src}
              alt="Cover"
              style={{ width:"100%", maxHeight:480, objectFit:"cover", display:"block", border:"1px solid #1f1f1f" }}
            />
          </div>
        )}

        {/* ── Live Site bar ── */}
        {project.websiteUrl && (
          <div style={{ marginBottom:32, background:"#0d0d0d", border:"1px solid #1f1f1f", borderTop: project.coverImage?.src ? "none" : "1px solid #1f1f1f", padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
            <div>
              <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:4 }}>
                {project.category === "Web Design" ? "Live Site" : project.category === "Product / UI Design" ? "Live Prototype" : "Project Link"}
              </div>
              <div style={{ fontSize:12, color:"#aaa", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:400 }}>{project.websiteUrl}</div>
            </div>
            <a href={safeUrl(project.websiteUrl)} target="_blank" rel="noopener noreferrer"
              style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 22px", border:"1px solid #fff", background:"#fff", color:"#000", fontSize:11, fontFamily:F.cond, fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", textDecoration:"none", transition:"all 0.15s", flexShrink:0 }}
              onMouseEnter={e=>{ e.currentTarget.style.background="#bbb"; e.currentTarget.style.borderColor="#bbb"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background="#fff"; e.currentTarget.style.borderColor="#fff"; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              {project.category === "Web Design" ? "Visit Live Site →" : project.category === "Product / UI Design" ? "View Prototype →" : "Visit Website →"}
            </a>
          </div>
        )}

        {/* ── Determine if this is a motion-first category ── */}
        {(() => {
          const MOTION_CATS = ["Motion Design","3D Render","VFX","Video Editing","Experiment"];
          const isMotion = MOTION_CATS.includes(project.category);

          const imgs = project.mediaImages?.length ? project.mediaImages : [];
          const vids = project.mediaVideos?.length ? project.mediaVideos
                     : (project.mediaPreview?.startsWith("data:video") || project.mediaType==="video"
                        ? [{ id:"legacy", src:project.mediaPreview }] : []);

          const imgCols = imgs.length === 1 ? 1 : imgs.length === 2 ? 2 : imgs.length === 3 ? 3 : 4;
          const isScreenshot = project.category === "Web Design" || project.category === "Product / UI Design";

          const ImageGrid = imgs.length > 0 ? (
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>
                {isScreenshot ? `Screenshots · ${imgs.length}` : `Images · ${imgs.length}`}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:`repeat(${imgCols},1fr)`, gap:4 }}>
                {imgs.map((img,i) => (
                  <div key={img.id||i} onClick={()=>setLightbox(i)}
                    style={{ position:"relative", paddingBottom:"75%", background:"#0d0d0d", border:"1px solid #1f1f1f", overflow:"hidden", cursor:"zoom-in" }}
                    title="Click to view fullscreen"
                  >
                    <img src={img.src} alt="" style={{ position:"absolute", top:0, left:0, right:0, bottom:0, width:"100%", height:"100%", objectFit:"cover", transition:"transform 0.2s" }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.03)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    />
                    <div style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.7)", border:"1px solid #333", padding:"2px 8px", display:"flex", alignItems:"center", gap:4, pointerEvents:"none" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          const VideoGrid = vids.length > 0 ? (
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>Videos · {vids.length}</div>
              <div style={{ display:"grid", gridTemplateColumns:vids.length===1?"1fr":"1fr 1fr", gap:8 }}>
                {vids.map((vid,i) => (
                  <div key={vid.id||i} style={{ background:"#000", border:"1px solid #1f1f1f", position:"relative" }}>
                    <video
                      src={vid.src}
                      controls
                      playsInline
                      style={{ width:"100%", display:"block", maxHeight:vids.length===1?520:280, background:"#000" }}
                      onPlay={e=>{ document.querySelectorAll("video").forEach(v=>{ if(v!==e.currentTarget) v.pause(); }); }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          const GifGrid = project.mediaGifs?.length > 0 ? (
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>Animated GIFs · {project.mediaGifs.length}</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:8 }}>
                {project.mediaGifs.map((gif,i) => (
                  <div key={gif.id||i} style={{ background:"#0d0d0d", border:"1px solid #1f1f1f", overflow:"hidden" }}>
                    <img src={gif.src} alt={gif.name||"GIF"} style={{ width:"100%", display:"block", objectFit:"cover" }} />
                    <div style={{ padding:"6px 10px", borderTop:"1px solid #1f1f1f" }}>
                      <span style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>GIF</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          const VideoLinkEmbed = (() => {
            if (!project.videoUrl) return null;
            const url = project.videoUrl;
            const vimeo = url.match(/vimeo\.com\/(\d+)/);
            const yt    = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
            const embedSrc = vimeo ? `https://player.vimeo.com/video/${vimeo[1]}` : yt ? `https://www.youtube.com/embed/${yt[1]}` : null;
            const label = vimeo ? "Watch on Vimeo ↗" : yt ? "Watch on YouTube ↗" : "Open Link ↗";
            const platform = vimeo ? "Vimeo" : yt ? "YouTube" : "Video Link";
            return (
              <div style={{ marginBottom:32, background:"#0d0d0d", border:"1px solid #1f1f1f" }}>
                <div style={{ padding:"10px 16px", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>{platform}</span>
                    <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:260 }}>{url}</span>
                  </div>
                  <a href={safeUrl(url)} target="_blank" rel="noopener noreferrer"
                    style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 14px", border:"1px solid #2e2e2e", background:"transparent", color:"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", transition:"all 0.15s", flexShrink:0 }}
                    onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    {label}
                  </a>
                </div>
                {embedSrc
                  ? <iframe src={embedSrc} style={{ width:"100%", height:480, border:"none", display:"block" }} allowFullScreen title="Video" />
                  : <div style={{ padding:"32px 24px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, minHeight:120 }}>
                      <div style={{ fontSize:32, color:"#2e2e2e" }}>▶</div>
                      <div style={{ fontSize:12, color:"#aaa", fontFamily:F.barlow, textAlign:"center" }}>Only YouTube and Vimeo can be embedded. Click the link above to watch directly.</div>
                    </div>
                }
              </div>
            );
          })();

          // Motion: video link first (if no uploaded video), then uploaded video, then GIFs, then images
          // Everything else: images first, then video
          return isMotion
            ? <>{vids.length === 0 ? VideoLinkEmbed : null}{VideoGrid}{ImageGrid}{GifGrid}</>
            : <>{ImageGrid}{VideoGrid}</>;
        })()}

        {/* ── PDF Viewer ── */}
        {(() => {
          const plist = project.mediaPdfs?.length ? project.mediaPdfs : [];
          if (!plist.length) return null;
          return (
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>
                Documents · {plist.length}
              </div>
              {plist.map((pdf, i) => (
                <div key={pdf.id||i} style={{ marginBottom:16, background:"#0d0d0d", border:"1px solid #1f1f1f" }}>
                  {/* PDF header bar */}
                  <div style={{ padding:"10px 16px", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:16, color:"#bbb" }}>⬚</span>
                      <span style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:300 }}>{pdf.name}</span>
                    </div>
                    {pdf.src && (
                      <a href={pdf.src} download={pdf.name}
                        style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 14px", border:"1px solid #2e2e2e", background:"transparent", color:"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", transition:"all 0.15s", flexShrink:0 }}
                        onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
                        onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
                      >
                        ↓ Download
                      </a>
                    )}
                  </div>
                  {/* PDF thumbnail preview — shown above the embedded viewer */}
                  {pdf.thumb && (
                    <div style={{ borderBottom:"1px solid #1f1f1f", overflow:"hidden", maxHeight:300, display:"flex", alignItems:"center", justifyContent:"center", background:"#000" }}>
                      <img src={pdf.thumb} alt="Page 1 preview" style={{ width:"100%", objectFit:"contain", display:"block", maxHeight:300 }} />
                    </div>
                  )}
                  {/* Embedded viewer or fallback */}
                  {pdf.src
                    ? <iframe
                        src={pdf.src}
                        style={{ width:"100%", height:600, border:"none", display:"block" }}
                        title={pdf.name}
                      />
                    : <div style={{ padding:"24px", display:"flex", alignItems:"center", gap:14 }}>
                        <span style={{ fontSize:28, color:"#2e2e2e" }}>⬚</span>
                        <div>
                          <div style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, marginBottom:4 }}>{pdf.name}</div>
                          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow }}>PDF preview not available — this was uploaded before viewer support was added.</div>
                        </div>
                      </div>
                  }
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── Figma Embed ── */}
        {project.figmaUrl && (() => {
          const url = project.figmaUrl;
          const isFigma = url.includes("figma.com");
          const embedSrc = isFigma ? `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}` : null;
          return (
            <div style={{ marginBottom:32, background:"#0d0d0d", border:"1px solid #1f1f1f" }}>
              <div style={{ padding:"10px 16px", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>Figma</span>
                  <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:260 }}>{url}</span>
                </div>
                <a href={safeUrl(url)} target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 14px", border:"1px solid #2e2e2e", background:"transparent", color:"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", transition:"all 0.15s", flexShrink:0 }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  {isFigma ? "Open in Figma ↗" : "Open Link ↗"}
                </a>
              </div>
              {embedSrc
                ? <iframe src={embedSrc} style={{ width:"100%", height:480, border:"none", display:"block" }} allowFullScreen title="Figma" />
                : <div style={{ padding:"32px 24px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, minHeight:120 }}>
                    <div style={{ fontSize:32, color:"#2e2e2e" }}>⬡</div>
                    <div style={{ fontSize:11, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>External Link</div>
                    <div style={{ fontSize:12, color:"#aaa", fontFamily:F.barlow, textAlign:"center", maxWidth:320 }}>This link can't be embedded. Click "Open Link ↗" above to visit it directly.</div>
                  </div>
              }
            </div>
          );
        })()}

        {/* ── Video Embed (Vimeo / YouTube) — non-motion categories only ── */}
        {project.videoUrl && !["Motion Design","3D Render","VFX","Video Editing","Experiment"].includes(project.category) && (() => {
          const url = project.videoUrl;
          const vimeo = url.match(/vimeo\.com\/(\d+)/);
          const yt    = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
          const embedSrc = vimeo ? `https://player.vimeo.com/video/${vimeo[1]}` : yt ? `https://www.youtube.com/embed/${yt[1]}` : null;
          const label = vimeo ? "Watch on Vimeo ↗" : yt ? "Watch on YouTube ↗" : "Open Link ↗";
          const platform = vimeo ? "Vimeo" : yt ? "YouTube" : "Video Link";
          return (
            <div style={{ marginBottom:32, background:"#0d0d0d", border:"1px solid #1f1f1f" }}>
              <div style={{ padding:"10px 16px", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>{platform}</span>
                  <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:260 }}>{url}</span>
                </div>
                <a href={safeUrl(url)} target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 14px", border:"1px solid #2e2e2e", background:"transparent", color:"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", transition:"all 0.15s", flexShrink:0 }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  {label}
                </a>
              </div>
              {embedSrc
                ? <iframe src={embedSrc} style={{ width:"100%", height:480, border:"none", display:"block" }} allowFullScreen title="Video" />
                : <div style={{ padding:"32px 24px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, minHeight:120 }}>
                    <div style={{ fontSize:32, color:"#2e2e2e" }}>▶</div>
                    <div style={{ fontSize:11, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>External Video Link</div>
                    <div style={{ fontSize:12, color:"#aaa", fontFamily:F.barlow, textAlign:"center", maxWidth:320 }}>Only YouTube and Vimeo can be embedded. Click "Open Link ↗" above to watch it directly.</div>
                  </div>
              }
            </div>
          );
        })()}

        {/* ── Legacy embed fallback (old projects that used mediaUrl/mediaTab) ── */}
        {!project.figmaUrl && !project.videoUrl && (project.mediaTab==="link"||project.mediaType==="link"||project.mediaTab==="figma"||project.mediaType==="figma") && project.mediaUrl && (() => {
          const url = project.mediaUrl;
          const vimeo = url.match(/vimeo\.com\/(\d+)/);
          const yt    = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
          const isFigma = url.includes("figma.com");
          const embedSrc = vimeo ? `https://player.vimeo.com/video/${vimeo[1]}` : yt ? `https://www.youtube.com/embed/${yt[1]}` : isFigma ? `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}` : url;
          const label = vimeo ? "Watch on Vimeo ↗" : yt ? "Watch on YouTube ↗" : isFigma ? "Open in Figma ↗" : "View Source ↗";
          const platform = vimeo ? "Vimeo" : yt ? "YouTube" : isFigma ? "Figma" : "Embedded";
          return (
            <div style={{ marginBottom:32, background:"#0d0d0d", border:"1px solid #1f1f1f" }}>
              <div style={{ padding:"10px 16px", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase" }}>{platform}</span>
                  <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:260 }}>{url}</span>
                </div>
                <a href={safeUrl(url)} target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 14px", border:"1px solid #2e2e2e", background:"transparent", color:"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", transition:"all 0.15s", flexShrink:0 }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  {label}
                </a>
              </div>
              <iframe src={embedSrc} style={{ width:"100%", height:480, border:"none", display:"block" }} allowFullScreen title={project.title} />
            </div>
          );
        })()}

        {/* ── No media placeholder ── */}
        {!project.mediaImages?.length && !project.mediaVideos?.length && !project.mediaPdfs?.length
          && !project.mediaPreview && !project.mediaUrl && (
          <div style={{ height:200, marginBottom:32, background:"#0d0d0d", border:"1px solid #1f1f1f", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12 }}>
            <div style={{ fontSize:64, color:"#1f1f1f" }}>{CAT_ICON[project.category]||"◎"}</div>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase" }}>No media uploaded yet</div>
          </div>
        )}

        {/* Title row */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:16, marginBottom:20 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>{project.category}</div>
            <h1 style={{ fontSize:30, fontWeight:900, color:"#fff", fontFamily:F.cond, margin:"0 0 12px", letterSpacing:"0.04em", textTransform:"uppercase", lineHeight:1 }}>{project.title}</h1>
            {project.category === "Tutorial" && (project.difficulty || project.duration) && (
              <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                {project.difficulty && <span style={{ fontSize:10, color:"#1d6bfc", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #1d6bfc", padding:"3px 10px" }}>{project.difficulty}</span>}
                {project.duration && <span style={{ fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"3px 10px" }}>{project.duration}</span>}
              </div>
            )}
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <Avatar member={author} size={28} />
              <span style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow }}>{author?.name}</span>
              <span style={{ color:"#2e2e2e" }}>·</span>
              <span style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow }}>{author?.role}</span>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:24 }}>
          {(project.tags||[]).map(t=>(
            <button key={t} onClick={()=>{ onTagClick(t); onClose(); }}
              style={{ padding:"3px 10px", border:"1px solid #2e2e2e", color:"#bbb", fontSize:11, fontFamily:F.cond, letterSpacing:"0.08em", textTransform:"uppercase", background:"transparent", cursor:"pointer", transition:"all 0.15s" }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor="#fff"; e.currentTarget.style.color="#fff"; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#bbb"; }}
              title={`Filter feed by "${t}"`}
            >{t}</button>
          ))}
        </div>

        {/* Description */}
        <div style={{ background:"#0d0d0d", border:"1px solid #1f1f1f", padding:"20px 24px", marginBottom:16 }}>
          <Label>About This Project</Label>
          <p style={{ fontSize:14, color:"#bbb", lineHeight:1.75, fontFamily:F.barlow, margin:0 }}>{project.description}</p>
        </div>

        {/* Challenges */}
        {project.challenges && (
          <div style={{ background:"#0d0d0d", border:"1px solid #1f1f1f", padding:"20px 24px", marginBottom:16 }}>
            <Label>Challenges</Label>
            <p style={{ fontSize:14, color:"#bbb", lineHeight:1.75, fontFamily:F.barlow, margin:0 }}>{project.challenges}</p>
          </div>
        )}



        <Divider />

        {/* Reactions */}
        <div style={{ marginBottom:32 }}>
          <Label>Reactions {totalReactions>0&&`· ${totalReactions}`}</Label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {EMOJI_REACTIONS.map(emoji=>{
              const count = project.reactions[emoji]||0;
              const active = userReactions.includes(emoji);
              return (
                <button key={emoji} onClick={()=>onReact(project.id,emoji)} style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 14px", background:"transparent", border:`1px solid ${active?"#fff":"#2e2e2e"}`, cursor:"pointer", transition:"border-color 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#bbb"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=active?"#fff":"#2e2e2e"}
                >
                  <span style={{ fontSize:18 }}>{emoji}</span>
                  {count>0&&<span style={{ fontSize:12, color:active?"#fff":"#bbb", fontFamily:F.barlow }}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <Divider />

        {/* Comments */}
        <div>
          <Label>Feedback · {project.comments.length}</Label>
          {project.comments.length===0 && <div style={{ fontSize:13, color:"#aaa", fontFamily:F.barlow, marginBottom:24 }}>No feedback yet. Be the first.</div>}

          {project.comments.map(c=>{
            const cAuthor    = getMember(c.authorId);
            const isMyComment = currentUser && c.authorId === currentUser.id;
            return (
              <CommentRow
                key={c.id}
                comment={c}
                author={cAuthor}
                isOwn={isMyComment}
                onEdit={newText => onEditComment(project.id, c.id, newText)}
                onDelete={() => onDeleteComment(project.id, c.id)}
              />
            );
          })}

          {/* Add comment — only when signed in */}
          {currentUser && (() => {
            const remaining = COMMENT_MAX - newComment.length;
            const isOverLimit = newComment.length > COMMENT_MAX;

            // @mention detection
            const mentionMatch = newComment.match(/@(\w*)$/);
            const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
            const mentionSuggestions = mentionQuery !== null
              ? allMembers.filter(m =>
                  m.id !== currentUser.id &&
                  (m.username?.toLowerCase().startsWith(mentionQuery) ||
                   m.name.toLowerCase().startsWith(mentionQuery))
                ).slice(0, 5)
              : [];

            const insertMention = (member) => {
              const replaced = newComment.replace(/@\w*$/, `@${member.username} `);
              setNewComment(replaced);
            };

            return (
              <div style={{ marginTop:8 }}>
                <div style={{ display:"flex", gap:10 }}>
                  <Avatar member={currentUser} size={32} />
                  <div style={{ flex:1, position:"relative" }}>
                    {/* @mention dropdown */}
                    {mentionSuggestions.length > 0 && (
                      <div style={{ position:"absolute", bottom:"100%", left:0, right:0, marginBottom:4, background:"#141414", border:"1px solid #2e2e2e", zIndex:100, boxShadow:"0 8px 24px rgba(0,0,0,0.8)" }}>
                        {mentionSuggestions.map(m => (
                          <button key={m.id} onClick={()=>insertMention(m)}
                            style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"none", border:"none", cursor:"pointer", textAlign:"left", borderBottom:"1px solid #1f1f1f" }}
                            onMouseEnter={e=>e.currentTarget.style.background="#1f1f1f"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}
                          >
                            <Avatar member={m} size={22} />
                            <div>
                              <div style={{ fontSize:12, color:"#fff", fontFamily:F.cond, letterSpacing:"0.04em", textTransform:"uppercase" }}>{m.name}</div>
                              <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow }}>@{m.username}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <div style={{ display:"flex", gap:8, marginBottom:4 }}>
                      <input value={newComment}
                        onChange={e=>setNewComment(e.target.value)}
                        onKeyDown={e=>{ if(e.key==="Enter"&&newComment.trim()&&!isOverLimit&&mentionSuggestions.length===0){ onComment(project.id,newComment); setNewComment(""); }}}
                        placeholder="Leave feedback… type @ to mention someone"
                        style={{ flex:1, background:"#0d0d0d", border:`1px solid ${isOverLimit?"#e05555":"#2e2e2e"}`, padding:"10px 14px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none" }}
                      />
                      <Btn variant="primary" onClick={()=>{ if(newComment.trim()&&!isOverLimit){ onComment(project.id,newComment); setNewComment(""); }}} style={{ padding:"10px 18px", opacity:isOverLimit?0.4:1 }}>Post</Btn>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow }}>Type @ to mention a teammate</span>
                      <span style={{ fontSize:10, fontFamily:F.barlow, color: isOverLimit?"#e05555": remaining<=50?"#bbb":"#aaa" }}>
                        {isOverLimit ? `${Math.abs(remaining)} over limit` : `${remaining} remaining`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          {!currentUser && (
            <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, padding:"12px 0" }}>
              <button onClick={()=>{}} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:12, fontFamily:F.barlow, textDecoration:"underline", padding:0 }}>Sign in</button> to leave feedback.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Profile page ─────────────────────────────────────────────────────────────

const ProfilePage = ({ member, projects, onClose, onEditProfile, onOpenProject, onOpenAdminPanel, isOwn }) => {
  if (!member) { console.error("ProfilePage: member is null"); return null; }
  const myProjects    = (projects||[]).filter(p=>p.authorId===member.id);
  const totalReactions = myProjects.reduce((a,p)=>a+Object.values(p.reactions||{}).reduce((x,y)=>x+y,0),0);
  const totalComments  = myProjects.reduce((a,p)=>a+(p.comments||[]).length,0);
  const bs = member.bannerScale || 1.0;

  return (
    <div style={{ minHeight:"100vh", background:"#000", overflowY:"auto" }}>

      {/* ── Banner + Hero ── */}
      <div style={{ position:"relative", background:"#0d0d0d", borderBottom:"1px solid #1f1f1f" }}>

        {/* Banner image layer */}
        <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, overflow:"hidden", pointerEvents:"none" }}>
          {member.bannerUrl ? (
            <img src={member.bannerUrl} alt="" draggable={false}
              style={{ position:"absolute", width:`${bs*100}%`, height:`${bs*100}%`, objectFit:"cover", objectPosition:member.bannerPosition||"50% 50%", opacity:member.bannerOpacity??0.5, top:"50%", left:"50%", transform:"translate(-50%,-50%)", userSelect:"none" }} />
          ) : (
            <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"repeating-linear-gradient(45deg,#0d0d0d 0px,#0d0d0d 20px,#111 20px,#111 40px)", opacity:0.4 }} />
          )}
          {/* Dark gradient overlay so text stays readable */}
          <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.95) 100%)" }} />
        </div>

        {/* Hero content */}
        <div style={{ position:"relative", maxWidth:960, margin:"0 auto", padding:"32px 24px 40px" }}>
          <Btn onClick={onClose} variant="ghost" style={{ marginBottom:36, padding:"7px 16px", fontSize:10 }}>← Back</Btn>

          <div style={{ display:"flex", gap:36, alignItems:"flex-end", flexWrap:"wrap" }}>
            {/* Avatar */}
            <div style={{ width:160, height:160, borderRadius:"50%", border:"3px solid #2e2e2e", overflow:"hidden", background:"#141414", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:"0 8px 40px rgba(0,0,0,0.7)" }}>
              {member.avatarUrl ? (
                <img src={member.avatarUrl} alt={member.name}
                  style={{ width:`${(member.avatarScale||1.4)*100}%`, height:`${(member.avatarScale||1.4)*100}%`, objectFit:"cover", objectPosition:member.avatarPosition||"center", flexShrink:0, pointerEvents:"none" }} />
              ) : (
                <span style={{ fontSize:56, fontWeight:900, color:"#aaa", fontFamily:F.cond }}>{member.initials}</span>
              )}
            </div>

            {/* Info */}
            <div style={{ flex:1, minWidth:200, paddingBottom:4 }}>
              <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>
                {isOwn ? "Your Profile" : "Team Member"}{member.isAdmin ? " · Admin" : ""}
              </div>
              <h1 style={{ fontSize:"clamp(26px,4vw,44px)", fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.04em", textTransform:"uppercase", margin:"0 0 4px", lineHeight:1 }}>{member.name}</h1>
              <div style={{ fontSize:14, color:"#bbb", fontFamily:F.barlow, marginBottom:4 }}>{member.role}</div>
              {member.username && <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, marginBottom:16 }}>@{member.username}</div>}
              {member.bio
                ? <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.75, maxWidth:520, marginBottom:20 }}>{member.bio}</p>
                : isOwn && <p style={{ fontSize:13, color:"#aaa", fontFamily:F.barlow, lineHeight:1.75, marginBottom:20, fontStyle:"italic" }}>No bio yet — add one in Edit Profile.</p>
              }
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                {member.behance   && <a href={safeUrl(member.behance)}   target="_blank" rel="noreferrer" style={{ fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"5px 12px", textDecoration:"none" }}>Behance ↗</a>}
                {member.linkedin  && <a href={safeUrl(member.linkedin)}  target="_blank" rel="noreferrer" style={{ fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"5px 12px", textDecoration:"none" }}>LinkedIn ↗</a>}
                {member.instagram && <a href={safeUrl(member.instagram)} target="_blank" rel="noreferrer" style={{ fontSize:10, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"5px 12px", textDecoration:"none" }}>Instagram ↗</a>}
                {isOwn && <Btn onClick={onEditProfile} variant="primary" style={{ padding:"6px 18px", fontSize:10 }}>Edit Profile →</Btn>}
                {isOwn && member.isAdmin && <Btn onClick={onOpenAdminPanel} variant="ghost" style={{ padding:"6px 14px", fontSize:10 }}>⚙ Admin Panel</Btn>}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display:"flex", gap:24, flexShrink:0, paddingBottom:4 }}>
              {[{l:"Projects",v:myProjects.length},{l:"Reactions",v:totalReactions},{l:"Feedback",v:totalComments}].map(s=>(
                <div key={s.l} style={{ textAlign:"right" }}>
                  <div style={{ fontSize:32, fontWeight:900, color:"#fff", fontFamily:F.cond, lineHeight:1 }}>{s.v}</div>
                  <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginTop:3 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:960, margin:"0 auto", padding:"40px 24px" }}>
        {/* Account info strip — own profile only */}
        {isOwn && (
          <div style={{ display:"flex", gap:1, marginBottom:40, background:"#1f1f1f", flexWrap:"wrap" }}>
            {[{label:"Email",value:member.email},{label:"Username",value:`@${member.username}`},{label:"Role",value:member.role}].map(item=>(
              <div key={item.label} style={{ flex:"1 1 180px", background:"#0d0d0d", padding:"14px 18px" }}>
                <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:5 }}>{item.label}</div>
                <div style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow }}>{item.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Projects */}
        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:20 }}>
          {isOwn ? "Your Projects" : `${member.name.split(" ")[0]}'s Projects`}
        </div>

        {myProjects.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:"#2e2e2e", borderTop:"1px solid #1f1f1f" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>◎</div>
            <div style={{ fontSize:11, fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>
              {isOwn ? "You haven't posted anything yet." : "No projects posted yet."}
            </div>
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:1, background:"#1f1f1f" }}>
            {myProjects.map(project=>{
              const author = member;
              const totalR = Object.values(project.reactions||{}).reduce((a,b)=>a+b,0);
              return (
                <div key={project.id} onClick={()=>onOpenProject(project)}
                  style={{ background:"#000", cursor:"pointer", display:"flex", flexDirection:"column", transition:"background 0.2s" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#0d0d0d"}
                  onMouseLeave={e=>e.currentTarget.style.background="#000"}
                >
                  {(() => {
                    const thumbImg = project.coverImage?.src || (project.videoUrl && project.videoUrl.match(/youtube\.com|youtu\.be/) ? videoThumb(project.videoUrl) : null) || project.mediaImages?.[0]?.src || (project.mediaPreview && (project.mediaPreview.startsWith("data:image") || project.mediaPreview.startsWith("https://")) ? project.mediaPreview : null) || project.mediaPdfs?.[0]?.thumb;
                    const thumbVid = project.mediaVideos?.[0]?.src || (project.mediaPreview?.startsWith("data:video") || project.mediaType==="video" ? project.mediaPreview : null);
                    const imgCount = project.mediaImages?.length || 0;
                    return (
                      <div style={{ height:160, background:"#0d0d0d", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
                        {thumbImg ? (
                          <><img src={thumbImg} alt={project.title} style={{ width:"100%", height:"100%", objectFit:"cover" }}
                            onError={e=>{
                              e.currentTarget.style.display="none";
                              const fb = e.currentTarget.parentNode.querySelector(".thumb-fallback");
                              if(fb) fb.style.display="flex";
                            }} />
                          <div className="thumb-fallback" style={{ display:"none", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20, textAlign:"center", width:"100%", height:"100%", position:"absolute", top:0, left:0 }}>
                            <div style={{ fontSize:9, color:"#1d6bfc", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:8 }}>{project.category}</div>
                            <div style={{ fontSize:16, fontWeight:900, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1.2, marginBottom:8 }}>{project.title}</div>
                            <div style={{ fontSize:9, color:"#888", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.12em", textTransform:"uppercase" }}>{author?.name}</div>
                          </div></>
                        ) : thumbVid ? (
                          <div style={{ width:"100%", height:"100%", position:"relative" }}>
                            <video src={thumbVid} style={{ width:"100%", height:"100%", objectFit:"cover" }} muted playsInline />
                            <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.35)" }}>
                              <div style={{ width:34, height:34, borderRadius:"50%", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><polygon points="5,3 19,12 5,21"/></svg>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize:40, color:"#1f1f1f" }}>{CAT_ICON[project.category]||"◎"}</span>
                        )}
                        <div style={{ position:"absolute", top:10, left:10, display:"flex", gap:6 }}>
                          <span style={{ padding:"2px 8px", border:"1px solid #2e2e2e", fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", background:"rgba(0,0,0,0.7)" }}>{project.category}</span>
                          
                        </div>
                        {imgCount > 1 && <div style={{ position:"absolute", bottom:8, left:10, background:"rgba(0,0,0,0.75)", border:"1px solid #333", padding:"2px 8px", fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em" }}>⊞ {imgCount} {project.category==="Web Design"||project.category==="Product / UI Design" ? "screenshots" : "images"}</div>}
                      </div>
                    );
                  })()}
                  <div style={{ padding:"16px 18px", flex:1 }}>
                    <h3 style={{ fontSize:14, fontWeight:800, color:"#fff", fontFamily:F.cond, letterSpacing:"0.04em", textTransform:"uppercase", lineHeight:1.2, marginBottom:8 }}>{project.title}</h3>
                    <p style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, lineHeight:1.6, marginBottom:10 }}>{project.description.slice(0,90)}{project.description.length>90?"…":""}</p>
                    <div style={{ display:"flex", gap:12, fontSize:11, color:"#aaa", fontFamily:F.barlow }}>
                      {totalR>0&&<span>{totalR} reactions</span>}
                      {project.comments.length>0&&<span>{project.comments.length} comments</span>}
                      <span style={{ marginLeft:"auto" }}>{timeAgo(project.createdAt || (project.id?.startsWith("p") ? parseInt(project.id.slice(1)) : null) || project.timestamp)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Admin Panel ──────────────────────────────────────────────────────────────

const AdminPanel = ({ members, projects, currentUser, onClose, onOpenProfile, onDeleteMember, onToggleAdmin, onResetStorage }) => {
  const [search, setSearch]           = useState("");
  const [confirmId, setConfirmId]     = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const filtered = members.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.username.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase()) ||
    m.role.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight:"100vh", background:"#000", overflowY:"auto" }}>
      <div style={{ maxWidth:900, margin:"0 auto", padding:"48px 24px" }}>

        {/* Header */}
        <Btn onClick={onClose} variant="ghost" style={{ marginBottom:32, padding:"7px 16px", fontSize:10 }}>← Back</Btn>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:8, flexWrap:"wrap", gap:12 }}>
          <div>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>Admin Only</div>
            <h1 style={{ fontSize:36, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase", lineHeight:1 }}>Member Management</h1>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
            <div style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow }}>{members.length} member{members.length!==1?"s":""} total</div>
            {!confirmReset
              ? <button onClick={()=>setConfirmReset(true)} style={{ background:"none", border:"1px solid #2e2e2e", color:"#aaa", cursor:"pointer", fontSize:9, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 12px", transition:"all 0.15s" }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor="#bbb"; e.currentTarget.style.color="#bbb"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#aaa"; }}
                >Reset Site Data</button>
              : <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <span style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow }}>Wipe all saved data?</span>
                  <button onClick={()=>{ onResetStorage(); setConfirmReset(false); }} style={{ background:"#fff", border:"none", color:"#000", cursor:"pointer", fontSize:9, fontFamily:F.cond, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 12px" }}>Yes, Reset</button>
                  <button onClick={()=>setConfirmReset(false)} style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:9, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"5px 12px" }}>Cancel</button>
                </div>
            }
          </div>
        </div>

        {/* Warning strip */}
        <div style={{ background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"10px 16px", marginBottom:32, display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ color:"#bbb", fontSize:14 }}>⚠</span>
          <span style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, lineHeight:1.5 }}>
            Deleting a member permanently removes their account and all their projects. This cannot be undone. You cannot delete yourself or other admins.
          </span>
        </div>

        {/* Search */}
        <div style={{ position:"relative", marginBottom:28 }}>
          <input
            value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by name, username, email, or role…"
            style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"11px 40px 11px 16px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" }}
          />
          <span style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", color:"#aaa", fontSize:15 }}>⌕</span>
          {search && <button onClick={()=>setSearch("")} style={{ position:"absolute", right:36, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:14, padding:0 }}>×</button>}
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:"#2e2e2e" }}>
            <div style={{ fontSize:32, marginBottom:10 }}>◎</div>
            <div style={{ fontSize:11, fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>No members match "{search}"</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:1, background:"#1f1f1f" }}>
            {filtered.map(m => {
              const mProjects = projects.filter(p=>p.authorId===m.id);
              const isSelf    = m.id === currentUser.id;
              const pending   = confirmId === m.id;
              const canAct    = !isSelf && !m.isAdmin;

              return (
                <div key={m.id} style={{ background:"#000", padding:"16px 20px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>

                  {/* Avatar + info */}
                  <button onClick={()=>onOpenProfile(m)} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:14, flex:1, minWidth:200, padding:0, textAlign:"left" }}>
                    <Avatar member={m} size={44} />
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase" }}>{m.name}</span>
                        {m.isAdmin && <span style={{ fontSize:8, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"2px 6px" }}>Admin</span>}
                        {isSelf   && <span style={{ fontSize:8, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", border:"1px solid #1f1f1f", padding:"2px 6px" }}>You</span>}
                      </div>
                      <div style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, marginTop:2 }}>@{m.username} · {m.role}</div>
                      <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:1 }}>{m.email} · {mProjects.length} project{mProjects.length!==1?"s":""}</div>
                    </div>
                  </button>

                  {/* Actions */}
                  <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
                    {/* Toggle admin — can't change self */}
                    {!isSelf && (
                      <button
                        onClick={()=>onToggleAdmin(m.id)}
                        style={{ background:"none", border:"1px solid #2e2e2e", color: m.isAdmin?"#bbb":"#aaa", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"6px 12px", transition:"all 0.15s" }}
                        onMouseEnter={e=>{ e.currentTarget.style.borderColor="#bbb"; e.currentTarget.style.color="#bbb"; }}
                        onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color=m.isAdmin?"#bbb":"#aaa"; }}
                        title={m.isAdmin?"Revoke admin access":"Grant admin access"}
                      >
                        {m.isAdmin ? "Revoke Admin" : "Make Admin"}
                      </button>
                    )}

                    {/* Delete — not self, not other admins */}
                    {canAct && !pending && (
                      <button
                        onClick={()=>setConfirmId(m.id)}
                        style={{ background:"none", border:"1px solid #2e2e2e", color:"#aaa", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"6px 12px", transition:"all 0.15s" }}
                        onMouseEnter={e=>{ e.currentTarget.style.borderColor="#bbb"; e.currentTarget.style.color="#bbb"; }}
                        onMouseLeave={e=>{ e.currentTarget.style.borderColor="#2e2e2e"; e.currentTarget.style.color="#aaa"; }}
                      >
                        Delete
                      </button>
                    )}

                    {/* Confirm delete */}
                    {canAct && pending && (
                      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                        <span style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow }}>Remove permanently?</span>
                        <button onClick={()=>{ onDeleteMember(m.id); setConfirmId(null); }}
                          style={{ background:"#fff", border:"1px solid #fff", color:"#000", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"6px 12px" }}>
                          Yes, Delete
                        </button>
                        <button onClick={()=>setConfirmId(null)}
                          style={{ background:"none", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:10, fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", padding:"6px 12px" }}>
                          Cancel
                        </button>
                      </div>
                    )}

                    {/* Non-actionable labels */}
                    {isSelf && <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, fontStyle:"italic" }}>Cannot modify self</span>}
                    {!isSelf && m.isAdmin && <span style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, fontStyle:"italic" }}>Cannot delete admin</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Members directory page ───────────────────────────────────────────────────

const MembersPage = ({ members, projects, onClose, onOpenProfile }) => {
  const [memberSearch, setMemberSearch] = useState("");
  const filtered = members.filter(m =>
    !memberSearch ||
    m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.role.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.username?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.bio?.toLowerCase().includes(memberSearch.toLowerCase())
  );
  return (
  <div style={{ minHeight:"100vh", background:"#000", overflowY:"auto" }}>
    <div style={{ maxWidth:1600, margin:"0 auto", padding:"48px 24px" }}>
      <Btn onClick={onClose} variant="ghost" style={{ marginBottom:36, padding:"7px 16px", fontSize:10 }}>← Back to Playgrounds</Btn>

      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontSize:"clamp(28px,4vw,52px)", fontWeight:900, fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase", color:"#fff", lineHeight:1, marginBottom:10 }}>Team Members</h1>
        <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.7 }}>
          {members.length} creative{members.length!==1?"s":""} building at OPSWAT. Click any card to view their profile and work.
        </p>
      </div>

      {/* Search */}
      <div style={{ position:"relative", marginBottom:32 }}>
        <input
          value={memberSearch}
          onChange={e=>setMemberSearch(e.target.value)}
          placeholder="Search by name, role, or username…"
          style={{ width:"100%", background:"#0d0d0d", border:"1px solid #2e2e2e", padding:"11px 40px 11px 16px", color:"#bbb", fontSize:13, fontFamily:F.barlow, outline:"none", boxSizing:"border-box" }}
        />
        <span style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", color:"#aaa", fontSize:15 }}>⌕</span>
        {memberSearch && <button onClick={()=>setMemberSearch("")} style={{ position:"absolute", right:36, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:14, padding:0 }}>×</button>}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#2e2e2e" }}>
          <div style={{ fontSize:32, marginBottom:10 }}>◎</div>
          <div style={{ fontSize:11, fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>No members match "{memberSearch}"</div>
        </div>
      ) : (
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:1, background:"#1f1f1f" }}>
        {filtered.map(m => {
          const mProjects  = projects.filter(p=>p.authorId===m.id);
          const mReactions = mProjects.reduce((a,p)=>a+Object.values(p.reactions||{}).reduce((x,y)=>x+y,0),0);
          const bs = m.bannerScale || 1.0;

          return (
            <div key={m.id} onClick={()=>onOpenProfile(m)}
              style={{ background:"#000", cursor:"pointer", display:"flex", flexDirection:"column", transition:"background 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.background="#0d0d0d"}
              onMouseLeave={e=>e.currentTarget.style.background="#000"}
            >
              {/* Banner strip */}
              <div style={{ height:80, background:"#0d0d0d", position:"relative", overflow:"hidden", flexShrink:0 }}>
                {m.bannerUrl ? (
                  <img src={m.bannerUrl} alt="" draggable={false}
                    style={{ position:"absolute", width:`${bs*100}%`, height:`${bs*100}%`, objectFit:"cover", objectPosition:m.bannerPosition||"50% 50%", opacity:m.bannerOpacity??0.5, top:"50%", left:"50%", transform:"translate(-50%,-50%)", userSelect:"none" }} />
                ) : (
                  <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"repeating-linear-gradient(45deg,#0d0d0d 0px,#0d0d0d 14px,#111 14px,#111 28px)", opacity:0.5 }} />
                )}
                <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 100%)" }} />
                {m.isAdmin && <div style={{ position:"absolute", top:8, right:8, fontSize:8, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"2px 7px", background:"rgba(0,0,0,0.7)" }}>Admin</div>}
              </div>

              {/* Avatar — overlapping banner */}
              <div style={{ padding:"0 18px", marginTop:-32, marginBottom:14, position:"relative" }}>
                <div style={{ width:64, height:64, borderRadius:"50%", border:"2px solid #000", overflow:"hidden", background:"#141414", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {m.avatarUrl ? (
                    <img src={m.avatarUrl} alt={m.name}
                      style={{ width:`${(m.avatarScale||1.4)*100}%`, height:`${(m.avatarScale||1.4)*100}%`, objectFit:"cover", objectPosition:m.avatarPosition||"center", pointerEvents:"none" }} />
                  ) : (
                    <span style={{ fontSize:22, fontWeight:900, color:"#aaa", fontFamily:F.cond }}>{m.initials}</span>
                  )}
                </div>
              </div>

              {/* Info */}
              <div style={{ padding:"0 18px 20px", flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                <div>
                  <div style={{ fontSize:16, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase", lineHeight:1.1 }}>{m.name}</div>
                  <div style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, marginTop:3 }}>{m.role}</div>
                  {m.username && <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:2 }}>@{m.username}</div>}
                </div>
                {m.bio && <p style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow, lineHeight:1.6, marginTop:4 }}>{m.bio.slice(0,80)}{m.bio.length>80?"…":""}</p>}
                <div style={{ display:"flex", gap:14, fontSize:11, color:"#aaa", fontFamily:F.barlow, marginTop:"auto", paddingTop:10, borderTop:"1px solid #1f1f1f" }}>
                  <span>{mProjects.length} project{mProjects.length!==1?"s":""}</span>
                  {mReactions > 0 && <span>{mReactions} reactions</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  </div>
  );
};

// ─── Edit Profile modal ───────────────────────────────────────────────────────

const BannerEditor = ({ bannerPreview, setBannerPreview, bannerPos, setBannerPos, bannerScale, setBannerScale, bannerOpacity, setBannerOpacity }) => {
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const fileRef   = useRef();

  const onMouseDown = e => { e.preventDefault(); setDragging(true); dragStart.current = { mx:e.clientX, my:e.clientY, px:bannerPos.x, py:bannerPos.y }; };
  const onMouseMove = e => {
    if (!dragging || !dragStart.current) return;
    const sens = 0.5 / bannerScale;
    setBannerPos({ x: Math.min(100, Math.max(0, dragStart.current.px - (e.clientX - dragStart.current.mx)*sens)), y: Math.min(100, Math.max(0, dragStart.current.py - (e.clientY - dragStart.current.my)*sens)) });
  };
  const onMouseUp = () => { setDragging(false); dragStart.current = null; };
  const onTouchStart = e => { const t=e.touches[0]; setDragging(true); dragStart.current={mx:t.clientX,my:t.clientY,px:bannerPos.x,py:bannerPos.y}; };
  const onTouchMove  = e => {
    if (!dragging || !dragStart.current) return;
    const t=e.touches[0]; const sens=0.5/bannerScale;
    setBannerPos({ x:Math.min(100,Math.max(0,dragStart.current.px-(t.clientX-dragStart.current.mx)*sens)), y:Math.min(100,Math.max(0,dragStart.current.py-(t.clientY-dragStart.current.my)*sens)) });
  };

  const handleFile = async e => {
    const f = e.target.files[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      setBannerPreview(ev.target.result);
      setBannerPos({ x:50, y:50 });
      setBannerScale(1.0);
    };
    reader.readAsDataURL(f);
  };

  return (
    <div onMouseMove={onMouseMove} onMouseUp={onMouseUp} onTouchMove={onTouchMove} onTouchEnd={onMouseUp}>
      <Label>Banner Image</Label>
      {/* Preview window */}
      <div style={{ position:"relative", width:"100%", height:140, background:"#0d0d0d", border:`1px solid ${dragging?"#fff":"#2e2e2e"}`, overflow:"hidden", marginBottom:12, cursor:bannerPreview?(dragging?"grabbing":"grab"):"default", transition:"border-color 0.15s", userSelect:"none" }}
        onMouseDown={bannerPreview ? onMouseDown : undefined}
        onTouchStart={bannerPreview ? onTouchStart : undefined}
      >
        {bannerPreview ? (
          <img src={bannerPreview} draggable={false} alt="banner"
            style={{ position:"absolute", width:`${bannerScale*100}%`, height:`${bannerScale*100}%`, objectFit:"cover", objectPosition:`${bannerPos.x}% ${bannerPos.y}%`, opacity:bannerOpacity, pointerEvents:"none", userSelect:"none", top:"50%", left:"50%", transform:"translate(-50%,-50%)" }} />
        ) : (
          <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:8 }}>
            <div style={{ fontSize:22, color:"#2e2e2e" }}>▭</div>
            <div style={{ fontSize:9, color:"#aaa", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase" }}>No Banner</div>
          </div>
        )}
        {bannerPreview && <div style={{ position:"absolute", bottom:6, right:8, fontSize:9, color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase" }}>Drag to reposition</div>}
        <div style={{ position:"absolute", top:6, left:8, fontSize:9, color:"rgba(255,255,255,0.2)", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase" }}>B&W only</div>
      </div>

      {/* Controls row */}
      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
        <Btn onClick={()=>fileRef.current.click()} variant="ghost" style={{ padding:"6px 14px", fontSize:10 }}>{bannerPreview?"Change Banner":"Upload Banner"}</Btn>
        {bannerPreview && <button onClick={()=>{ setBannerPreview(""); setBannerPos({x:50,y:50}); setBannerScale(1.0); setBannerOpacity(0.5); }} style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:11, fontFamily:"'Barlow',sans-serif", textDecoration:"underline", padding:0 }}>Remove</button>}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:"none" }} />
      </div>
      <div style={{ fontSize:9, color:"#aaa", fontFamily:"'Barlow',sans-serif", marginBottom:14 }}>Uploads are auto-converted to black & white.</div>

      {bannerPreview && (<>
        {/* Scale slider */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
            <span style={{ fontSize:9, color:"#bbb", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase" }}>Scale</span>
            <span style={{ fontSize:10, color:"#bbb", fontFamily:"'Barlow',sans-serif" }}>{Math.round((bannerScale-1)/2*100)}%</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:10, color:"#aaa" }}>−</span>
            <input type="range" min="1.0" max="3.0" step="0.05" value={bannerScale} onChange={e=>setBannerScale(parseFloat(e.target.value))} style={{ flex:1, accentColor:"#fff", cursor:"pointer" }} />
            <span style={{ fontSize:10, color:"#aaa" }}>+</span>
          </div>
        </div>
        {/* Opacity slider */}
        <div style={{ marginBottom:4 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
            <span style={{ fontSize:9, color:"#bbb", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase" }}>Opacity</span>
            <span style={{ fontSize:10, color:"#bbb", fontFamily:"'Barlow',sans-serif" }}>{Math.round(bannerOpacity*100)}%</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:10, color:"#aaa" }}>0</span>
            <input type="range" min="0.05" max="1.0" step="0.05" value={bannerOpacity} onChange={e=>setBannerOpacity(parseFloat(e.target.value))} style={{ flex:1, accentColor:"#fff", cursor:"pointer" }} />
            <span style={{ fontSize:10, color:"#aaa" }}>100</span>
          </div>
        </div>
      </>)}
    </div>
  );
};

const EditProfileModal = ({ member, onClose, onSave, members }) => {
  const [form, setForm] = useState({
    name:      member.name,
    username:  member.username  || "",
    role:      member.role,
    bio:       member.bio      || "",
    behance:   member.behance  || "",
    linkedin:  member.linkedin || "",
    instagram: member.instagram|| "",
  });
  const [usernameError, setUsernameError] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(member.avatarUrl || "");
  const [imgPos,   setImgPos]   = useState({ x:50, y:50 });
  const [imgScale, setImgScale] = useState(member.avatarScale || 1.4);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const fileRef   = useRef();

  // Banner state
  const [bannerPreview, setBannerPreview] = useState(member.bannerUrl || "");
  const [bannerPos,     setBannerPos]     = useState({ x:50, y:50 });
  const [bannerScale,   setBannerScale]   = useState(member.bannerScale  || 1.0);
  const [bannerOpacity, setBannerOpacity] = useState(member.bannerOpacity ?? 0.5);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    if (member.avatarPosition) {
      const parts = member.avatarPosition.replace(/%/g,"").split(" ");
      if (parts.length===2) setImgPos({ x:parseFloat(parts[0]), y:parseFloat(parts[1]) });
    }
    if (member.avatarScale) setImgScale(member.avatarScale);
    if (member.bannerPosition) {
      const p = member.bannerPosition.replace(/%/g,"").split(" ");
      if (p.length===2) setBannerPos({ x:parseFloat(p[0]), y:parseFloat(p[1]) });
    }
  }, []);

  const handleFile = e => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = ev => setAvatarPreview(ev.target.result);
    r.readAsDataURL(f);
    setImgPos({ x:50, y:50 });
    setImgScale(1.4);
  };

  const onMouseDown = e => { e.preventDefault(); setDragging(true); dragStart.current = { mx:e.clientX, my:e.clientY, px:imgPos.x, py:imgPos.y }; };
  const onMouseMove = e => {
    if (!dragging || !dragStart.current) return;
    const sens = 0.5/imgScale;
    setImgPos({ x:Math.min(100,Math.max(0, dragStart.current.px-(e.clientX-dragStart.current.mx)*sens)), y:Math.min(100,Math.max(0, dragStart.current.py-(e.clientY-dragStart.current.my)*sens)) });
  };
  const onMouseUp = () => { setDragging(false); dragStart.current = null; };
  const onTouchStart = e => { const t=e.touches[0]; setDragging(true); dragStart.current={mx:t.clientX,my:t.clientY,px:imgPos.x,py:imgPos.y}; };
  const onTouchMove  = e => {
    if (!dragging||!dragStart.current) return;
    const t=e.touches[0]; const sens = 0.5/imgScale;
    setImgPos({ x:Math.min(100,Math.max(0,dragStart.current.px-(t.clientX-dragStart.current.mx)*sens)), y:Math.min(100,Math.max(0,dragStart.current.py-(t.clientY-dragStart.current.my)*sens)) });
  };

  const avatarPosition = `${imgPos.x}% ${imgPos.y}%`;
  const bannerPosition = `${bannerPos.x}% ${bannerPos.y}%`;
  const imgSize = `${imgScale*100}%`;

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:1500, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20, overflowY:"auto" }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onTouchMove={onTouchMove} onTouchEnd={onMouseUp}
    >
      <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:560, boxShadow:"0 24px 80px rgba(0,0,0,0.9)", margin:"auto" }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28 }}>
          <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>Edit Profile</div>
          <button onClick={onClose} style={{ background:"#1f1f1f", border:"1px solid #2e2e2e", color:"#bbb", cursor:"pointer", fontSize:16, width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        {/* ── Profile Photo ── */}
        <Section>
          <Label>Profile Photo</Label>
          <div style={{ display:"flex", gap:24, alignItems:"flex-start", flexWrap:"wrap" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, flexShrink:0 }}>
              <div style={{ width:160, height:160, borderRadius:"50%", background:"#0d0d0d", border:`2px solid ${dragging?"#fff":"#2e2e2e"}`, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", cursor:avatarPreview?(dragging?"grabbing":"grab"):"default", transition:"border-color 0.15s", userSelect:"none", flexShrink:0 }}
                onMouseDown={avatarPreview ? onMouseDown : undefined} onTouchStart={avatarPreview ? onTouchStart : undefined}
              >
                {avatarPreview
                  ? <img src={avatarPreview} alt="avatar" draggable={false} style={{ width:imgSize, height:imgSize, objectFit:"cover", objectPosition:avatarPosition, pointerEvents:"none", userSelect:"none", flexShrink:0 }} />
                  : <span style={{ fontSize:52, fontWeight:900, color:"#aaa", fontFamily:F.cond }}>{member.initials}</span>
                }
              </div>
              {avatarPreview && <div style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase" }}>Drag to reposition</div>}
            </div>
            <div style={{ flex:1, minWidth:180 }}>
              <div style={{ fontSize:12, color:"#bbb", fontFamily:F.barlow, lineHeight:1.65, marginBottom:16 }}>Upload a photo, drag to reposition, and use the scale slider to zoom in or out.</div>
              <Btn onClick={()=>fileRef.current.click()} variant="ghost" style={{ padding:"8px 16px", marginBottom:12, width:"100%", textAlign:"center" }}>{avatarPreview?"Change Photo":"Upload Photo"}</Btn>
              {avatarPreview && (<>
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                    <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>Scale</span>
                    <span style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow }}>{Math.round((imgScale-1)/2*100)}%</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:10, color:"#aaa" }}>−</span>
                    <input type="range" min="1.0" max="3.0" step="0.05" value={imgScale} onChange={e=>setImgScale(parseFloat(e.target.value))} style={{ flex:1, accentColor:"#fff", cursor:"pointer" }} />
                    <span style={{ fontSize:10, color:"#aaa" }}>+</span>
                  </div>
                </div>
                <button onClick={()=>{ setAvatarPreview(""); setImgPos({x:50,y:50}); setImgScale(1.4); }} style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:11, fontFamily:F.barlow, textDecoration:"underline", padding:0 }}>Remove photo</button>
              </>)}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display:"none" }} />
              <div style={{ fontSize:10, color:"#2e2e2e", fontFamily:F.barlow, marginTop:8 }}>JPG or PNG recommended</div>
            </div>
          </div>
        </Section>

        {/* ── Banner Image ── */}
        <Section>
          <BannerEditor
            bannerPreview={bannerPreview} setBannerPreview={setBannerPreview}
            bannerPos={bannerPos}         setBannerPos={setBannerPos}
            bannerScale={bannerScale}     setBannerScale={setBannerScale}
            bannerOpacity={bannerOpacity} setBannerOpacity={setBannerOpacity}
          />
        </Section>

        <Section><Label>Name</Label><Input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Your name" /></Section>
        <Section><Label>Role</Label><Input value={form.role} onChange={e=>set("role",e.target.value)} placeholder="Your role" /></Section>
        <Section><Label>Bio</Label><Textarea value={form.bio} onChange={e=>set("bio",e.target.value)} placeholder="A short bio" rows={3} /></Section>

        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:10 }}>Account</div>
        <Section>
          <Label>Username</Label>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:"#aaa", fontSize:13, fontFamily:F.barlow, pointerEvents:"none" }}>@</span>
            <Input
              value={form.username}
              onChange={e => {
                const val = e.target.value.replace(/\s/g, "").toLowerCase();
                setForm(f => ({...f, username: val}));
                setUsernameError("");
              }}
              placeholder="yourhandle"
              style={{ paddingLeft:28 }}
            />
          </div>
          {usernameError && <div style={{ fontSize:11, color:"#e05555", fontFamily:F.barlow, marginTop:6 }}>{usernameError}</div>}
          <div style={{ fontSize:10, color:"#aaa", fontFamily:F.barlow, marginTop:6 }}>No spaces. Lowercase only. This is how others find and mention you.</div>
        </Section>

        <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:10 }}>Social Links</div>
        <Section><Label>Behance</Label><Input value={form.behance}   onChange={e=>set("behance",e.target.value)}   placeholder="https://behance.net/yourname" /></Section>
        <Section><Label>LinkedIn</Label><Input value={form.linkedin}  onChange={e=>set("linkedin",e.target.value)}  placeholder="https://linkedin.com/in/yourname" /></Section>
        <Section><Label>Instagram</Label><Input value={form.instagram} onChange={e=>set("instagram",e.target.value)} placeholder="https://instagram.com/yourhandle" /></Section>

        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <Btn onClick={onClose} variant="ghost" style={{ flex:1 }}>Cancel</Btn>
          <Btn onClick={()=>{
            const trimmed = form.username.trim().toLowerCase();
            if (!trimmed) { setUsernameError("Username cannot be empty."); return; }
            if (trimmed.length < 3) { setUsernameError("Username must be at least 3 characters."); return; }
            const taken = (members||[]).find(m => m.id !== member.id && m.username?.toLowerCase() === trimmed);
            if (taken) { setUsernameError("That username is already taken. Please choose another."); return; }
            onSave({ ...form, username: trimmed, avatarUrl:avatarPreview, avatarPosition, avatarScale:imgScale, bannerUrl:bannerPreview, bannerPosition, bannerScale, bannerOpacity });
          }} variant="primary" style={{ flex:2 }}>Save Changes →</Btn>
        </div>
      </div>
    </div>
  );
};

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  // ── Supabase data loading ────────────────────────────────────────────────────
  const [members, setMembersRaw]         = useState(SEED_MEMBERS);
  const [currentUser, setCurrentUserRaw] = useState(null);
  const [showSignUp, setShowSignUp]       = useState(false);
  const [showSignIn, setShowSignIn]       = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(null);
  const [showUserMenu, setShowUserMenu]   = useState(false);
  const [storageReady, setStorageReady]   = useState(false);
  const [loading, setLoading]             = useState(true);

  const setMembers = (updater) => {
    setMembersRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
  };

  const setCurrentUser = (user) => {
    setCurrentUserRaw(user);
    if (user) {
      try { localStorage.setItem("opswat_pg_currentUser", JSON.stringify(user)); } catch(e) {}
    } else {
      try { localStorage.removeItem("opswat_pg_currentUser"); } catch(e) {}
    }
  };

  // Load data from Supabase on first mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const { data: membersData } = await supabase.from("members").select("*");
        if (membersData && membersData.length > 0) {
          setMembersRaw(membersData.map(m => ({
            id: m.id, name: m.name, initials: m.initials, role: m.role, bio: m.bio,
            email: m.email, username: m.username, password: m.password,
            isAdmin: m.is_admin, avatarUrl: m.avatar_url, avatarPosition: m.avatar_position,
            avatarScale: m.avatar_scale, bannerUrl: m.banner_url, bannerPosition: m.banner_position,
            bannerScale: m.banner_scale, bannerOpacity: m.banner_opacity,
            behance: m.behance, linkedin: m.linkedin, instagram: m.instagram,
          })));
        } else {
          // First time - seed the admin account
          await supabase.from("members").insert(SEED_MEMBERS.map(m => ({
            id: m.id, name: m.name, initials: m.initials, role: m.role, bio: m.bio,
            email: m.email, username: m.username, password: m.password,
            is_admin: m.isAdmin || false, avatar_url: m.avatarUrl || "",
            avatar_position: m.avatarPosition || "50% 50%", avatar_scale: m.avatarScale || 1.4,
            banner_url: m.bannerUrl || "", banner_position: m.bannerPosition || "50% 50%",
            banner_scale: m.bannerScale || 1.0, banner_opacity: m.bannerOpacity || 0.5,
            behance: m.behance || "", linkedin: m.linkedin || "", instagram: m.instagram || "",
          })));
          setMembersRaw(SEED_MEMBERS);
        }

        // Load only essential fields first for fast initial render
        // Load full media lazily when project is opened
        const { data: projectsData } = await supabase
          .from("projects")
          .select("id, title, author_id, category, description, challenges, tags, difficulty, duration, website_url, video_url, media_preview, cover_image, media_images, media_videos, media_gifs, media_pdfs, reactions, user_reactions, is_new, timestamp, created_at, comments(*)")
          .order("created_at", { ascending: false })
          .limit(50);
        if (projectsData) {
          setProjectsRaw(projectsData.map(p => ({
            id: p.id, title: p.title, authorId: p.author_id, category: p.category,
            description: p.description, challenges: p.challenges, tags: p.tags || [],
            difficulty: p.difficulty, duration: p.duration, websiteUrl: p.website_url,
            videoUrl: p.video_url, mediaPreview: p.media_preview,
            coverImage: p.cover_image, mediaImages: p.media_images || [],
            mediaVideos: p.media_videos || [], mediaGifs: p.media_gifs || [],
            mediaPdfs: p.media_pdfs || [], reactions: p.reactions || {},
            userReactions: p.user_reactions || {}, isNew: p.is_new,
            timestamp: p.timestamp,
            comments: (p.comments || []).map(c => ({
              id: c.id, authorId: c.author_id, text: c.text, timestamp: c.created_at,
            })),
          })));
        }

        // Restore current user from localStorage
        try {
          const u = localStorage.getItem("opswat_pg_currentUser");
          if (u) { const saved = JSON.parse(u); if (saved) setCurrentUserRaw(saved); }
        } catch(e) {}

      } catch(err) {
        console.error("Failed to load from Supabase:", err);
      }
      setLoading(false);
      setStorageReady(true);
    };
    loadData().then(() => {
      // After data loads, check for ?project= share link
      const params = new URLSearchParams(window.location.search);
      const pid = params.get("project");
      if (pid) {
        setProjectsRaw(prev => {
          const found = prev.find(p => p.id === pid);
          if (found) setTimeout(() => setSelectedProject(found), 200);
          return prev;
        });
      }
    });

    // Real-time listener - refresh projects when any change happens
    const subscription = supabase
      .channel("all-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "members" }, () => {
        supabase.from("members").select("*").then(({ data }) => {
          if (data) {
            const mapped = data.map(m => ({
              id: m.id, name: m.name || "", initials: m.initials || "", role: m.role || "", bio: m.bio || "",
              email: m.email || "", username: m.username || "", password: m.password || "",
              isAdmin: m.is_admin || false, avatarUrl: m.avatar_url || "", avatarPosition: m.avatar_position || "50% 50%",
              avatarScale: parseFloat(m.avatar_scale) || 1.4, bannerUrl: m.banner_url || "", bannerPosition: m.banner_position || "50% 50%",
              bannerScale: parseFloat(m.banner_scale) || 1.0, bannerOpacity: parseFloat(m.banner_opacity) ?? 0.5,
              behance: m.behance || "", linkedin: m.linkedin || "", instagram: m.instagram || "",
            }));
            setMembersRaw(mapped);
            // Update currentUser if their profile changed
            setCurrentUserRaw(prev => {
              if (!prev) return prev;
              const updated = mapped.find(m => m.id === prev.id);
              if (!updated) return null; // member was deleted - sign them out
              const merged = { ...prev, ...updated };
              try { localStorage.setItem("opswat_pg_currentUser", JSON.stringify(merged)); } catch(e) {}
              return merged;
            });
          }
        });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "projects" }, () => {
        supabase.from("projects").select("id, title, author_id, category, description, challenges, tags, difficulty, duration, website_url, video_url, media_preview, cover_image, media_images, media_videos, media_gifs, media_pdfs, reactions, user_reactions, is_new, timestamp, created_at, comments(*)").order("created_at", { ascending: false }).limit(50).then(({ data }) => {
          if (data) {
            setProjectsRaw(data.map(p => ({
              id: p.id, title: p.title || "", authorId: p.author_id, category: p.category || "",
              description: p.description || "", challenges: p.challenges || "", tags: p.tags || [],
              difficulty: p.difficulty || "", duration: p.duration || "", websiteUrl: p.website_url || "",
              videoUrl: p.video_url || "", mediaPreview: p.media_preview || "",
              coverImage: p.cover_image || null, mediaImages: p.media_images || [],
              mediaVideos: p.media_videos || [], mediaGifs: p.media_gifs || [],
              mediaPdfs: p.media_pdfs || [], reactions: p.reactions || {},
              userReactions: p.user_reactions || {}, isNew: p.is_new || false,
              timestamp: p.timestamp || "just now",
              createdAt: p.created_at || null,
              comments: (p.comments || []).map(c => ({
                id: c.id, authorId: c.author_id, text: c.text, timestamp: c.created_at,
              })),
            })));
          }
        });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "comments" }, () => {
        supabase.from("projects").select("id, title, author_id, category, description, challenges, tags, difficulty, duration, website_url, video_url, media_preview, cover_image, media_images, media_videos, media_gifs, media_pdfs, reactions, user_reactions, is_new, timestamp, created_at, comments(*)").order("created_at", { ascending: false }).limit(50).then(({ data }) => {
          if (data) {
            setProjectsRaw(data.map(p => ({
              id: p.id, title: p.title || "", authorId: p.author_id, category: p.category || "",
              description: p.description || "", challenges: p.challenges || "", tags: p.tags || [],
              difficulty: p.difficulty || "", duration: p.duration || "", websiteUrl: p.website_url || "",
              videoUrl: p.video_url || "", mediaPreview: p.media_preview || "",
              coverImage: p.cover_image || null, mediaImages: p.media_images || [],
              mediaVideos: p.media_videos || [], mediaGifs: p.media_gifs || [],
              mediaPdfs: p.media_pdfs || [], reactions: p.reactions || {},
              userReactions: p.user_reactions || {}, isNew: p.is_new || false,
              timestamp: p.timestamp || "just now",
              createdAt: p.created_at || null,
              comments: (p.comments || []).map(c => ({
                id: c.id, authorId: c.author_id, text: c.text, timestamp: c.created_at,
              })),
            })));
          }
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(subscription); };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return;
    const handler = () => setShowUserMenu(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [showUserMenu]);

  // Data
  const [projects, setProjectsRaw]       = useState(SEED_PROJECTS);

  const setProjects = (updater) => {
    setProjectsRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
  };

  // Views
  const [view, setView]                 = useState("feed"); // feed | profile | members | admin
  const [profileMember, setProfileMember] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Modals
  const [showPost, setShowPost]         = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // Feed controls
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery]   = useState("");
  const [activeTag,   setActiveTag]     = useState("");
  const [sortBy,      setSortBy]        = useState("newest"); // newest | reactions | comments
  const [toast, setToast]               = useState(null);

  const showToast = (label, title, sub, duration=5000) => {
    setToast({ label, title, sub });
    setTimeout(()=>setToast(null), duration);
  };

  const getMember = id => members.find(m=>m.id===id);

  const filtered = projects
    .filter(p => {
      const matchCat    = activeCategory==="All" || p.category===activeCategory;
      const matchSearch = !searchQuery
        || p.title.toLowerCase().includes(searchQuery.toLowerCase())
        || (p.tags||[]).some(t=>t.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchTag = !activeTag
        || (p.tags||[]).some(t=>t.toLowerCase()===activeTag.toLowerCase());
      return matchCat && matchSearch && matchTag;
    })
    .sort((a,b) => {
      if (sortBy === "reactions") {
        const ar = Object.values(a.reactions||{}).reduce((x,y)=>x+y,0);
        const br = Object.values(b.reactions||{}).reduce((x,y)=>x+y,0);
        return br - ar;
      }
      if (sortBy === "comments") return (b.comments?.length||0) - (a.comments?.length||0);
      return 0; // newest — already in newest-first order from projects array
    });

  // ── Handlers ──

  const handleSignUp = async (form) => {
    const newMember = {
      id: `m${Date.now()}`,
      name: form.name,
      initials: form.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
      role: form.role, bio: form.bio || "", behance: form.behance || "", linkedin: form.linkedin || "",
      instagram: "", avatarUrl: form.avatarUrl || "",
      avatarPosition: "50% 50%", avatarScale: 1.4,
      bannerUrl: "", bannerPosition: "50% 50%", bannerScale: 1.0, bannerOpacity: 0.5,
      email: form.email.toLowerCase().trim(),
      username: form.username.toLowerCase().trim(),
      password: form.password,
      isAdmin: false,
    };
    try {
      await supabase.from("members").insert({
        id: newMember.id, name: newMember.name, initials: newMember.initials,
        role: newMember.role, bio: newMember.bio, email: newMember.email,
        username: newMember.username, password: newMember.password,
        is_admin: false, avatar_url: newMember.avatarUrl,
        avatar_position: "50% 50%", avatar_scale: 1.4,
        banner_url: "", banner_position: "50% 50%", banner_scale: 1.0, banner_opacity: 0.5,
        behance: newMember.behance, linkedin: newMember.linkedin, instagram: "",
      });
    } catch(err) { console.error("Signup error:", err); }
    setMembers(ms=>[...ms, newMember]);
    setCurrentUser(newMember);
    setShowSignUp(false);
    showToast("Welcome", newMember.name.toUpperCase(), "Your account has been created.");
  };

  const handleSignIn = (member) => {
    setCurrentUser(member);
    setShowSignIn(false);
    if (member.isAdmin) {
      setShowEmailConfirm(member);
    } else {
      showToast("Welcome Back", member.name.toUpperCase(), `Signed in as @${member.username}`);
    }
  };

  const handlePasswordReset = (id, newPassword) => {
    setMembers(ms => ms.map(m => m.id === id ? { ...m, password: newPassword } : m));
    showToast("Password Updated", "Success", "Your password has been changed. Sign in with your new password.");
  };

  const handleDeleteMember = async (id) => {
    try {
      await supabase.from("projects").delete().eq("author_id", id);
      await supabase.from("members").delete().eq("id", id);
    } catch(err) { console.error("Delete member error:", err); }
    setMembers(ms => ms.filter(m => m.id !== id));
    setProjects(ps => ps.filter(p => p.authorId !== id));
    if (profileMember?.id === id) setView("admin");
    showToast("Member Removed", "Account Deleted", "The member and all their projects have been removed.");
  };

  const handleToggleAdmin = async (id) => {
    const target = members.find(m => m.id === id);
    const newAdminState = !target?.isAdmin;
    try {
      await supabase.from("members").update({ is_admin: newAdminState }).eq("id", id);
    } catch(err) { console.error("Toggle admin error:", err); }
    setMembers(ms => ms.map(m => m.id === id ? { ...m, isAdmin: newAdminState } : m));
    if (target) showToast("Admin Updated", target.name.toUpperCase(), newAdminState ? "Admin access granted." : "Admin access revoked.");
  };

  const handleResetStorage = async () => {
    try {
      await supabase.from("comments").delete().neq("id", "");
      await supabase.from("projects").delete().neq("id", "");
      await supabase.from("members").delete().neq("id", "");
      await supabase.from("members").insert(SEED_MEMBERS.map(m => ({
        id: m.id, name: m.name, initials: m.initials, role: m.role, bio: m.bio,
        email: m.email, username: m.username, password: m.password,
        is_admin: m.isAdmin || false, avatar_url: m.avatarUrl || "",
        avatar_position: "50% 50%", avatar_scale: 1.4,
        banner_url: "", banner_position: "50% 50%", banner_scale: 1.0, banner_opacity: 0.5,
        behance: "", linkedin: "", instagram: "",
      })));
      localStorage.removeItem("opswat_pg_currentUser");
    } catch(e) { console.error("Reset error:", e); }
    setMembersRaw(SEED_MEMBERS);
    setProjectsRaw(SEED_PROJECTS);
    setCurrentUserRaw(null);
    setView("feed");
    showToast("Data Reset", "Fresh Start", "All saved data cleared. Seed data restored.");
  };

  const handleSignOut = () => {
    setCurrentUser(null);
    setView("feed");
    setSelectedProject(null);
    showToast("Signed Out", "See you next time", "");
  };

  const handlePost = async (form) => {
    const pid = `p${Date.now()}`;

    // Upload all media files to Supabase Storage
    const uploadMedia = async (items, folder) => {
      if (!items || items.length === 0) return [];
      return Promise.all(items.map(async (item) => {
        if (!item.src || !item.src.startsWith("data:")) return item;
        const url = await uploadImage(item.src, `${folder}/${pid}`);
        return { ...item, src: url };
      }));
    };

    const coverImage = form.coverImage?.src?.startsWith("data:")
      ? { ...form.coverImage, src: await uploadImage(form.coverImage.src, `covers/${pid}`) }
      : form.coverImage || null;

    const mediaImages = await uploadMedia(form.mediaImages, "images");
    const mediaGifs   = await uploadMedia(form.mediaGifs,   "gifs");
    const mediaVideos = await uploadMedia(form.mediaVideos, "videos");
    const mediaPdfs   = await uploadMedia(form.mediaPdfs,   "pdfs");

    const mediaPreview = coverImage?.src || mediaImages?.[0]?.src || form.mediaPreview || "";

    const newProject = {
      id: pid, title:form.title, authorId:currentUser.id,
      category:form.category, description:form.description, challenges:form.challenges,
      tags:form.tags.split(",").map(t=>t.trim()).filter(Boolean),
      difficulty: form.difficulty || "",
      duration: form.duration || "",
      mediaType:form.mediaType, mediaTab:form.mediaTab, mediaUrl:form.mediaUrl,
      figmaUrl: form.figmaUrl || "",
      videoUrl: form.videoUrl || "",
      websiteUrl: form.websiteUrl || "",
      coverImage,
      mediaPreview,
      mediaImages, mediaVideos, mediaGifs, mediaPdfs,
      reactions:{}, userReactions:{},
      comments:[], timestamp:"just now", isNew:true,
    };
    try {
      const { error } = await supabase.from("projects").insert({
        id: newProject.id, title: newProject.title, author_id: newProject.authorId,
        category: newProject.category, description: newProject.description,
        challenges: newProject.challenges, tags: newProject.tags,
        difficulty: newProject.difficulty, duration: newProject.duration,
        website_url: newProject.websiteUrl, video_url: newProject.videoUrl,
        media_preview: newProject.mediaPreview, cover_image: newProject.coverImage,
        media_images: mediaImages, media_videos: mediaVideos,
        media_gifs: mediaGifs, media_pdfs: mediaPdfs,
        reactions: {}, user_reactions: {}, is_new: true, timestamp: "just now",
      });
      if (error) console.error("Supabase insert error:", error);
    } catch(err) { console.error("Post error:", err); }
    setProjects(ps=>[newProject,...ps]);
    setShowPost(false);
    showToast("New Upload", newProject.title.toUpperCase(), `Posted by ${currentUser.name}`);
  };

  const handleReact = async (id, emoji) => {
    if (!currentUser) return;
    const project = projects.find(p => p.id === id);
    if (!project) return;
    const userR = project.userReactions?.[currentUser.id] || [];
    const has = userR.includes(emoji);
    const newUserReactions = { ...project.userReactions, [currentUser.id]: has ? userR.filter(e=>e!==emoji) : [...userR, emoji] };
    const newReactions = { ...project.reactions, [emoji]: (project.reactions[emoji]||0) + (has ? -1 : 1) };
    try {
      await supabase.from("projects").update({ reactions: newReactions, user_reactions: newUserReactions }).eq("id", id);
    } catch(err) { console.error("React error:", err); }
    setProjects(ps=>ps.map(p=>{
      if(p.id!==id) return p;
      return { ...p, userReactions: newUserReactions, reactions: newReactions };
    }));
    setSelectedProject(sp=>{
      if(!sp||sp.id!==id) return sp;
      return { ...sp, userReactions: newUserReactions, reactions: newReactions };
    });
  };

  const handleComment = async (id, text) => {
    if (!currentUser) return;
    const c = { id:`c${Date.now()}`, authorId:currentUser.id, text, time:"just now" };
    try {
      await supabase.from("comments").insert({ id: c.id, project_id: id, author_id: currentUser.id, text });
    } catch(err) { console.error("Comment error:", err); }
    setProjects(ps=>ps.map(p=>p.id!==id?p:{ ...p, comments:[...p.comments,c] }));
    setSelectedProject(sp=>sp?.id===id?{ ...sp, comments:[...sp.comments,c] }:sp);
  };

  const handleEditComment = (projectId, commentId, newText) => {
    const update = ps => ps.map(p => p.id!==projectId ? p : {
      ...p, comments: p.comments.map(c => c.id!==commentId ? c : { ...c, text:newText, edited:true })
    });
    setProjects(update);
    setSelectedProject(sp => sp?.id===projectId ? update([sp])[0] : sp);
  };

  const handleDeleteComment = async (projectId, commentId) => {
    try { await supabase.from("comments").delete().eq("id", commentId); } catch(err) { console.error(err); }
    const update = ps => ps.map(p => p.id!==projectId ? p : {
      ...p, comments: p.comments.filter(c => c.id!==commentId)
    });
    setProjects(update);
    setSelectedProject(sp => sp?.id===projectId ? update([sp])[0] : sp);
  };

  const handleRequestFeedback = (project) => {
    setProjects(ps=>ps.map(p=>p.id===project.id?{ ...p, feedbackRequested:true }:p));
    setSelectedProject(sp=>sp?.id===project.id?{ ...sp, feedbackRequested:true }:sp);
    setShowEmailPreview({ project:{ ...project, feedbackRequested:true }, author:getMember(project.authorId) });
  };

  const handleEditProfile = async (updates) => {
    // Upload avatar and banner to Supabase Storage if they are base64
    const avatarUrl = await uploadImage(updates.avatarUrl, `avatars/${currentUser.id}`);
    const bannerUrl = await uploadImage(updates.bannerUrl, `banners/${currentUser.id}`);
    const updated = { ...currentUser, ...updates, avatarUrl, bannerUrl };
    try {
      await supabase.from("members").update({
        name: updated.name, role: updated.role, bio: updated.bio,
        avatar_url: avatarUrl, avatar_position: updated.avatarPosition,
        avatar_scale: updated.avatarScale, banner_url: bannerUrl,
        banner_position: updated.bannerPosition, banner_scale: updated.bannerScale,
        banner_opacity: updated.bannerOpacity, behance: updated.behance,
        linkedin: updated.linkedin, instagram: updated.instagram,
      }).eq("id", currentUser.id);
    } catch(err) { console.error("Profile update error:", err); }
    setMembers(ms=>ms.map(m=>m.id===currentUser.id ? updated : m));
    setCurrentUser(updated);
    setProfileMember(updated);
    setShowEditProfile(false);
    showToast("Profile Updated", updated.name.toUpperCase(), "Your changes have been saved.");
  };

  const handleDelete = async (id) => {
    try { await supabase.from("projects").delete().eq("id", id); } catch(err) { console.error(err); }
    setProjects(ps => ps.filter(p => p.id !== id));
    setSelectedProject(null);
    showToast("Deleted", "Project Removed", "The project has been permanently deleted.");
  };

  const handleUpdateProject = async (updated) => {
    // Upload any base64 media to Supabase Storage before saving
    const uploadMediaArr = async (items, folder) => {
      if (!items || items.length === 0) return [];
      return Promise.all(items.map(async (item) => {
        if (!item.src || !item.src.startsWith("data:")) return item;
        const url = await uploadImage(item.src, `${folder}/${updated.id}`);
        return { ...item, src: url };
      }));
    };

    const coverImage = updated.coverImage?.src?.startsWith("data:")
      ? { ...updated.coverImage, src: await uploadImage(updated.coverImage.src, `covers/${updated.id}`) }
      : updated.coverImage || null;

    const mediaImages = await uploadMediaArr(updated.mediaImages, "images");
    const mediaGifs   = await uploadMediaArr(updated.mediaGifs,   "gifs");
    const mediaVideos = await uploadMediaArr(updated.mediaVideos, "videos");
    const mediaPdfs   = await uploadMediaArr(updated.mediaPdfs,   "pdfs");
    const mediaPreview = coverImage?.src || mediaImages?.[0]?.src || updated.mediaPreview || "";

    const final = { ...updated, coverImage, mediaImages, mediaGifs, mediaVideos, mediaPdfs, mediaPreview };

    try {
      await supabase.from("projects").update({
        title: final.title, category: final.category, description: final.description,
        challenges: final.challenges, tags: final.tags, difficulty: final.difficulty,
        duration: final.duration, website_url: final.websiteUrl, video_url: final.videoUrl,
        media_preview: final.mediaPreview, cover_image: final.coverImage,
        media_images: final.mediaImages, media_videos: final.mediaVideos,
        media_gifs: final.mediaGifs, media_pdfs: final.mediaPdfs,
        reactions: final.reactions, user_reactions: final.userReactions,
      }).eq("id", final.id);
    } catch(err) { console.error("Update error:", err); }
    setProjects(ps => ps.map(p => p.id === final.id ? final : p));
    setSelectedProject(final);
    showToast("Saved", final.title.toUpperCase(), "Your project has been updated.");
  };

  const openProfile = (member) => {
    if (!member) return;
    // Always use fresh data from members array if available
    const fresh = members.find(m => m.id === member.id) || member;
    setProfileMember(fresh);
    setSelectedProject(null);
    setView("profile");
  };

  const openProject = (p) => setSelectedProject(projects.find(x=>x.id===p.id));

  // Show minimal loading screen while storage hydrates
  if (!storageReady) return (
    <div style={{ minHeight:"100vh", background:"#000", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <div style={{ fontSize:9, color:"#aaa", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.25em", textTransform:"uppercase" }}>OPSWAT Playgrounds</div>
      <div style={{ width:32, height:1, background:"#2e2e2e" }} />
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"#000", fontFamily:F.barlow }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:3px; }
        ::-webkit-scrollbar-track { background:#000; }
        ::-webkit-scrollbar-thumb { background:#2e2e2e; border-radius:2px; }
        input::placeholder,textarea::placeholder { color:#333; }
        select option { background:#0d0d0d; }
        @keyframes slideIn { from{transform:translateX(40px);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        .p-card { animation:fadeUp 0.35s ease both; }
        a:hover { color:#fff !important; border-color:#fff !important; }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          /* Header */
          .pg-header-inner { padding: 0 16px !important; height: auto !important; flex-wrap: wrap; gap: 8px; padding-top: 10px !important; padding-bottom: 10px !important; }
          .pg-nav { display: none !important; }
          .pg-search { width: 100% !important; order: 3; }
          .pg-right { gap: 6px !important; }
          .pg-post-btn { padding: 7px 12px !important; font-size: 10px !important; }
          .pg-username { display: none !important; }

          /* Main feed */
          .pg-main { padding: 24px 16px !important; }
          .pg-hero h1 { font-size: 32px !important; white-space: normal !important; }
          .pg-stats { gap: 20px !important; margin-bottom: 28px !important; padding-bottom: 28px !important; }
          .pg-filters { gap: 6px !important; flex-direction: column !important; align-items: flex-start !important; }
          .pg-sort-controls { width: 100% !important; justify-content: flex-start !important; flex-wrap: wrap !important; }

          /* Cards — single column on mobile */
          .pg-feed-grid { grid-template-columns: 1fr !important; }
          .pg-members-grid { grid-template-columns: 1fr !important; }
          .pg-profile-grid { grid-template-columns: 1fr !important; }

          /* Project detail */
          .pg-detail { padding: 20px 16px !important; }
          .pg-detail-topbar { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .pg-detail-title { font-size: 22px !important; }
          .pg-live-bar { flex-direction: column !important; gap: 10px !important; }
          .pg-live-bar a { width: 100% !important; justify-content: center !important; }

          /* Modals — full screen on mobile */
          .pg-modal-wrap { padding: 0 !important; align-items: flex-end !important; }
          .pg-modal-box { border-radius: 0 !important; padding: 24px 20px !important; max-height: 90vh; overflow-y: auto; }

          /* Profile page */
          .pg-profile-hero { padding: 24px 16px 32px !important; }
          .pg-profile-avatar { width: 100px !important; height: 100px !important; }
          .pg-profile-stats { gap: 16px !important; }

          /* Edit profile modal */
          .pg-edit-profile-layout { flex-direction: column !important; }

          /* Post modal tabs — wrap on mobile */
          .pg-media-tabs { gap: 4px !important; }
          .pg-media-tabs button { font-size: 9px !important; padding: 4px 8px !important; }

          /* Category pills */
          .pg-category-pills { overflow-x: auto !important; flex-wrap: nowrap !important; padding-bottom: 4px !important; }
        }

        @media (max-width: 480px) {
          .pg-hero h1 { font-size: 26px !important; }
          .pg-modal-box { padding: 20px 16px !important; }
          .pg-detail-title { font-size: 18px !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <header style={{ position:"sticky", top:0, zIndex:100, background:"rgba(0,0,0,0.97)", backdropFilter:"blur(12px)", borderBottom:"1px solid #1f1f1f", padding:"0 32px" }}>
        <div className="pg-header-inner" style={{ maxWidth:1600, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", height:58 }}>

          {/* Logo */}
          <button onClick={()=>{ setView("feed"); setSelectedProject(null); }} style={{ display:"flex", alignItems:"center", gap:10, background:"none", border:"none", cursor:"pointer", padding:0 }}>
            <div style={{ width:26, height:26, background:"#1d6bfc", border:"1px solid #1d6bfc", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"#fff" }}>◈</div>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontSize:13, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.12em", lineHeight:1, textTransform:"uppercase" }}>OPSWAT</div>
              <div style={{ fontSize:8, color:"#bbb", letterSpacing:"0.2em", fontFamily:F.cond, fontWeight:700, textTransform:"uppercase" }}>Playgrounds</div>
            </div>
          </button>

          {/* Center nav */}
          <div className="pg-nav" style={{ display:"flex", gap:4 }}>
            <button onClick={()=>{ setView("feed"); setSelectedProject(null); }}
              style={{ background:"none", border:"none", cursor:"pointer", padding:"6px 14px", fontSize:12, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", color: view==="feed"?"#1d6bfc":"#bbb", borderBottom: view==="feed"?"1px solid #1d6bfc":"1px solid transparent", transition:"all 0.15s" }}>
              Feed
            </button>
            <button onClick={()=>{ setView("members"); setSelectedProject(null); }}
              style={{ background:"none", border:"none", cursor:"pointer", padding:"6px 14px", fontSize:12, fontFamily:F.cond, letterSpacing:"0.12em", textTransform:"uppercase", color: view==="members"?"#1d6bfc":"#bbb", borderBottom: view==="members"?"1px solid #1d6bfc":"1px solid transparent", transition:"all 0.15s" }}>
              Members
            </button>
          </div>

          {/* Right controls */}
          <div className="pg-right" style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div className="pg-search" style={{ position:"relative" }}>
              <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search..."
                style={{ background:"#0d0d0d", border:"1px solid #1f1f1f", padding:"7px 14px 7px 32px", color:"#bbb", fontSize:12, fontFamily:F.barlow, outline:"none", width:180 }} />
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#aaa", fontSize:13 }}>⌕</span>
            </div>

            {currentUser ? (
              <>
                {/* Avatar dropdown */}
                <div style={{ position:"relative" }}>
                  <button
                    onClick={e=>{ e.stopPropagation(); setShowUserMenu(v=>!v); }}
                    style={{ background:"none", border:"1px solid transparent", cursor:"pointer", padding:"4px 8px 4px 4px", display:"flex", alignItems:"center", gap:8, transition:"border-color 0.15s", borderRadius:2 }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor="#2e2e2e"}
                    onMouseLeave={e=>{ if(!showUserMenu) e.currentTarget.style.borderColor="transparent"; }}
                  >
                    <Avatar member={currentUser} size={30} />
                    <span className="pg-username" style={{ fontSize:11, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase" }}>@{currentUser.username}</span>
                    <span style={{ fontSize:9, color:"#bbb", marginLeft:2 }}>▾</span>
                  </button>

                  {/* Dropdown */}
                  {showUserMenu && (
                    <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 8px)", right:0, background:"#141414", border:"1px solid #2e2e2e", minWidth:200, zIndex:500, boxShadow:"0 12px 40px rgba(0,0,0,0.8)" }}>

                      {/* User info header */}
                      <div style={{ padding:"14px 16px", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", gap:10 }}>
                        <Avatar member={currentUser} size={36} />
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#fff", fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase" }}>{currentUser.name}</div>
                          <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginTop:1 }}>{currentUser.role}</div>
                          {currentUser.isAdmin && <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", marginTop:2 }}>Admin</div>}
                        </div>
                      </div>

                      {/* Menu items */}
                      {[
                        { label:"My Profile",    icon:"◈", action:()=>{ openProfile(members.find(m=>m.id===currentUser?.id) || currentUser); setShowUserMenu(false); } },
                        { label:"Edit Profile",  icon:"✎", action:()=>{ setShowEditProfile(true); setShowUserMenu(false); } },
                        { label:"Post Work",     icon:"+", action:()=>{ setShowPost(true); setShowUserMenu(false); } },
                      ].map(item=>(
                        <button key={item.label} onClick={item.action}
                          style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"11px 16px", background:"none", border:"none", cursor:"pointer", textAlign:"left", borderBottom:"1px solid #1f1f1f", transition:"background 0.15s" }}
                          onMouseEnter={e=>e.currentTarget.style.background="#1f1f1f"}
                          onMouseLeave={e=>e.currentTarget.style.background="none"}
                        >
                          <span style={{ fontSize:12, color:"#bbb", width:14, textAlign:"center" }}>{item.icon}</span>
                          <span style={{ fontSize:12, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.08em", textTransform:"uppercase" }}>{item.label}</span>
                        </button>
                      ))}

                      {/* Sign out */}
                      <button onClick={()=>{ setShowSignOutConfirm(true); setShowUserMenu(false); }}
                        style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"11px 16px", background:"none", border:"none", cursor:"pointer", textAlign:"left", transition:"background 0.15s" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#1f1f1f"}
                        onMouseLeave={e=>e.currentTarget.style.background="none"}
                      >
                        <span style={{ fontSize:12, color:"#bbb", width:14, textAlign:"center" }}>→</span>
                        <span style={{ fontSize:12, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.08em", textTransform:"uppercase" }}>Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>

                <Btn onClick={()=>setShowPost(true)} variant="primary" style={{ padding:"8px 16px" }}>+ Post Work</Btn>
              </>
            ) : (
              <>
                <Btn onClick={()=>setShowSignIn(true)} variant="ghost" style={{ padding:"7px 14px", fontSize:10 }}>Sign In</Btn>
                <Btn onClick={()=>setShowSignUp(true)} variant="primary" style={{ padding:"8px 22px", whiteSpace:"nowrap" }}>Join OPSWAT Playgrounds</Btn>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Feed view ── */}
      {view === "feed" && (
        <main className="pg-main" style={{ maxWidth:1600, margin:"0 auto", padding:"52px 32px" }}>

          {/* Headline */}
          <div className="pg-hero" style={{ marginBottom:52 }}>
            <h1 style={{ fontSize:"clamp(32px,5.5vw,72px)", fontWeight:900, fontFamily:F.cond, letterSpacing:"0.04em", lineHeight:1, textTransform:"uppercase", marginBottom:18, whiteSpace:"nowrap" }}>
              <span style={{ color:"#fff" }}>OPSWAT CREATIVE </span>
              <span style={{ color:"#1d6bfc" }}>SANDBOX</span>
            </h1>
            <p style={{ fontSize:13, color:"#bbb", maxWidth:420, lineHeight:1.8, fontFamily:F.barlow }}>
              Post experiments, WIPs, and finished work. Leave feedback. Get reactions. Build in the open.
            </p>
          </div>



          {/* Filters + Sort */}
          <div className="pg-filters" style={{ display:"flex", gap:8, marginBottom:activeTag?8:28, flexWrap:"wrap", alignItems:"center", justifyContent:"space-between" }}>
            <div className="pg-category-pills" style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {CATEGORIES.map(c=><CategoryPill key={c} label={c} active={activeCategory===c} onClick={()=>{ setActiveCategory(c); setActiveTag(""); }} />)}
            </div>
            {/* Sort controls */}
            <div className="pg-sort-controls" style={{ display:"flex", gap:6, flexShrink:0 }}>
              {[{id:"newest",label:"Newest"},{id:"reactions",label:"Top Reactions"},{id:"comments",label:"Most Comments"}].map(s=>(
                <button key={s.id} onClick={()=>setSortBy(s.id)}
                  style={{ padding:"5px 12px", border:"1px solid", borderColor:sortBy===s.id?"#1d6bfc":"#2e2e2e", background:sortBy===s.id?"#1d6bfc":"transparent", color:sortBy===s.id?"#fff":"#bbb", fontSize:10, fontFamily:F.cond, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active tag filter chip */}
          {activeTag && (
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
              <span style={{ fontSize:9, color:"#aaa", fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>Filtering by tag:</span>
              <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"3px 10px", border:"1px solid #fff", background:"transparent" }}>
                <span style={{ fontSize:11, color:"#fff", fontFamily:F.cond, letterSpacing:"0.08em", textTransform:"uppercase" }}>{activeTag}</span>
                <button onClick={()=>setActiveTag("")} style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:14, padding:0, lineHeight:1 }}>×</button>
              </div>
            </div>
          )}

          {/* Grid */}
          <div className="pg-feed-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:1, background:"#1f1f1f" }}>
            {filtered.map((project,i)=>{
              const author = getMember(project.authorId);
              const totalReactions = Object.values(project.reactions).reduce((a,b)=>a+b,0);
              return (
                <div key={project.id} className="p-card" style={{ animationDelay:`${i*0.06}s` }}>
                  <div onClick={()=>openProject(project)} style={{ background:"#000", cursor:"pointer", display:"flex", flexDirection:"column", height:"100%", transition:"background 0.2s" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#0d0d0d"}
                    onMouseLeave={e=>e.currentTarget.style.background="#000"}
                  >
                    {/* Thumbnail */}
                    {(() => {
                      const thumbImg = project.coverImage?.src || (project.videoUrl && project.videoUrl.match(/youtube\.com|youtu\.be/) ? videoThumb(project.videoUrl) : null) || project.mediaImages?.[0]?.src || (project.mediaPreview && (project.mediaPreview.startsWith("data:image") || project.mediaPreview.startsWith("https://")) ? project.mediaPreview : null) || project.mediaPdfs?.[0]?.thumb;
                      const thumbVid = project.mediaVideos?.[0]?.src || (project.mediaPreview?.startsWith("data:video") || project.mediaType==="video" ? project.mediaPreview : null);
                      const imgCount = project.mediaImages?.length || 0;
                      const vidCount = project.mediaVideos?.length || (thumbVid ? 1 : 0);
                      return (
                        <div style={{ height:200, background:"#0d0d0d", borderBottom:"1px solid #1f1f1f", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
                          {thumbImg ? (
                            <><img src={thumbImg} alt={project.title} style={{ width:"100%", height:"100%", objectFit:"cover" }}
                            onError={e=>{
                              e.currentTarget.style.display="none";
                              const fb = e.currentTarget.parentNode.querySelector(".thumb-fallback");
                              if(fb) fb.style.display="flex";
                            }} />
                          <div className="thumb-fallback" style={{ display:"none", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20, textAlign:"center", width:"100%", height:"100%", position:"absolute", top:0, left:0 }}>
                            <div style={{ fontSize:9, color:"#1d6bfc", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:8 }}>{project.category}</div>
                            <div style={{ fontSize:16, fontWeight:900, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1.2, marginBottom:8 }}>{project.title}</div>
                            <div style={{ fontSize:9, color:"#888", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.12em", textTransform:"uppercase" }}>{author?.name}</div>
                          </div></>
                          ) : thumbVid ? (
                            <div style={{ width:"100%", height:"100%", position:"relative" }}>
                              <video src={thumbVid} style={{ width:"100%", height:"100%", objectFit:"cover" }} muted playsInline />
                              <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.35)" }}>
                                <div style={{ width:40, height:40, borderRadius:"50%", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><polygon points="5,3 19,12 5,21"/></svg>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20, textAlign:"center" }}>
                              <div style={{ fontSize:9, color:"#1d6bfc", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:8 }}>{project.category}</div>
                              <div style={{ fontSize:16, fontWeight:900, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1.2, marginBottom:8 }}>{project.title}</div>
                              <div style={{ fontSize:9, color:"#888", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:"0.12em", textTransform:"uppercase" }}>{author?.name}</div>
                            </div>
                          )}
                          <div style={{ position:"absolute", top:12, left:12, display:"flex", gap:6, flexWrap:"wrap" }}>
                            <span style={{ padding:"2px 8px", border:"1px solid #2e2e2e", fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", background:"rgba(0,0,0,0.7)" }}>{project.category}</span>
                            
                            
                          </div>
                          {/* Image / video count badges */}
                          {imgCount > 1 && <div style={{ position:"absolute", bottom:10, left:12, background:"rgba(0,0,0,0.75)", border:"1px solid #333", padding:"2px 8px", fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em" }}>⊞ {imgCount} {project.category==="Web Design"||project.category==="Product / UI Design" ? "screenshots" : "images"}</div>}
                          {vidCount > 0 && imgCount === 0 && vidCount > 1 && <div style={{ position:"absolute", bottom:10, left:12, background:"rgba(0,0,0,0.75)", border:"1px solid #333", padding:"2px 8px", fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em" }}>▶ {vidCount} videos</div>}
                          <div style={{ position:"absolute", bottom:10, right:12, fontSize:10, color:"#bbb", fontFamily:F.barlow, textShadow:"0 1px 3px rgba(0,0,0,0.9)" }}>{timeAgo(project.createdAt || (project.id?.startsWith("p") ? parseInt(project.id.slice(1)) : null) || project.timestamp)}</div>
                        </div>
                      );
                    })()}

                    <div style={{ padding:"18px 20px", flex:1, display:"flex", flexDirection:"column", gap:12 }}>
                      <h3 style={{ fontSize:16, fontWeight:800, color:"#fff", fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase", lineHeight:1.2 }}>{project.title}</h3>
                      {project.category === "Tutorial" && (project.difficulty || project.duration) && (
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          {project.difficulty && <span style={{ fontSize:9, color:"#1d6bfc", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #1d6bfc", padding:"2px 8px" }}>{project.difficulty}</span>}
                          {project.duration && <span style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e2e2e", padding:"2px 8px" }}>{project.duration}</span>}
                        </div>
                      )}
                      <p style={{ fontSize:12, color:"#bbb", lineHeight:1.7, fontFamily:F.barlow, flex:1 }}>
                        {project.description.slice(0,110)}{project.description.length>110?"…":""}
                      </p>

                      {/* Author */}
                      <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:12, borderTop:"1px solid #1f1f1f" }}>
                        <button onClick={e=>{e.stopPropagation();openProfile(author);}} style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", alignItems:"center", gap:8 }}>
                          <Avatar member={author} size={22} />
                          <span style={{ fontSize:11, color:"#bbb", fontFamily:F.barlow }}>{author?.name}</span>
                        </button>
                      </div>

                      {(totalReactions>0||project.comments.length>0) && (
                        <div style={{ display:"flex", gap:14, fontSize:11, color:"#aaa", fontFamily:F.barlow }}>
                          {totalReactions>0&&<span>{totalReactions} reactions</span>}
                          {project.comments.length>0&&<span>{project.comments.length} comments</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length===0&&(
            <div style={{ textAlign:"center", padding:"80px 0", color:"#2e2e2e" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>◎</div>
              <div style={{ fontSize:11, fontFamily:F.cond, letterSpacing:"0.15em", textTransform:"uppercase" }}>Nothing here yet. Be the first to post.</div>
            </div>
          )}

          {/* Team members */}
          <div style={{ marginTop:64, paddingTop:40, borderTop:"1px solid #1f1f1f" }}>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:20 }}>Team Members</div>
            <div style={{ display:"flex", gap:1, flexWrap:"wrap", background:"#1f1f1f" }}>
              {members.map(m=>(
                <button key={m.id} onClick={()=>openProfile(m)} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", background:"#000", flex:"1 1 180px", border:"none", cursor:"pointer", transition:"background 0.15s", textAlign:"left" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#0d0d0d"}
                  onMouseLeave={e=>e.currentTarget.style.background="#000"}
                >
                  <Avatar member={m} size={30} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.05em", textTransform:"uppercase" }}>{m.name}</div>
                    <div style={{ fontSize:10, color:"#bbb", fontFamily:F.barlow, marginTop:2 }}>{m.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </main>
      )}

      {/* ── Admin panel view ── */}
      {view === "admin" && currentUser?.isAdmin && (
        <AdminPanel
          members={members}
          projects={projects}
          currentUser={currentUser}
          onClose={()=>setView(profileMember ? "profile" : "feed")}
          onOpenProfile={m=>{ setProfileMember(m); setView("profile"); }}
          onDeleteMember={handleDeleteMember}
          onToggleAdmin={handleToggleAdmin}
          onResetStorage={handleResetStorage}
        />
      )}

      {/* ── Members directory view ── */}
      {view === "members" && (
        <MembersPage
          members={members}
          projects={projects}
          onClose={()=>setView("feed")}
          onOpenProfile={m=>{ setProfileMember(m); setView("profile"); }}
        />
      )}

      {/* ── Profile view ── */}
      {view === "profile" && profileMember && (
        <ProfilePage
          member={members.find(m=>m.id===profileMember.id) || profileMember}
          projects={projects}
          onClose={()=>setView("feed")}
          onEditProfile={()=>setShowEditProfile(true)}
          onOpenProject={p=>{ openProject(p); }}
          onOpenAdminPanel={()=>setView("admin")}
          isOwn={!!currentUser && profileMember.id===currentUser.id}
        />
      )}

      {/* ── Overlays ── */}
      {selectedProject && (
        <ProjectDetail
          project={selectedProject}
          getMember={getMember}
          allMembers={members}
          currentUser={currentUser}
          onClose={()=>setSelectedProject(null)}
          onReact={handleReact}
          onComment={handleComment}
          onEditComment={handleEditComment}
          onDeleteComment={handleDeleteComment}
          onRequestFeedback={handleRequestFeedback}
          onDelete={handleDelete}
          onUpdate={handleUpdateProject}
          showToast={showToast}
          onTagClick={tag=>{ setActiveTag(tag); setActiveCategory("All"); }}
        />
      )}

      {showPost       && currentUser && <PostModal onClose={()=>setShowPost(false)} onSubmit={handlePost} />}
      {showSignUp     && <SignUpModal   onClose={()=>setShowSignUp(false)}  onSignUp={handleSignUp} />}
      {showSignIn     && <SignInModal   onClose={()=>setShowSignIn(false)}  onSignIn={handleSignIn} onGoSignUp={()=>{ setShowSignIn(false); setShowSignUp(true); }} members={members} onPasswordReset={handlePasswordReset} />}

      {/* Sign out confirmation */}
      {showSignOutConfirm && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:2000, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:"#141414", border:"1px solid #2e2e2e", padding:"36px 32px", width:"100%", maxWidth:400, boxShadow:"0 24px 80px rgba(0,0,0,0.9)" }}>
            <div style={{ fontSize:9, color:"#bbb", fontFamily:F.cond, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>OPSWAT Playgrounds</div>
            <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:F.cond, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:12 }}>Sign Out?</div>
            <p style={{ fontSize:13, color:"#bbb", fontFamily:F.barlow, lineHeight:1.7, marginBottom:28 }}>
              You'll need to sign back in to post work or leave feedback.
            </p>
            <div style={{ display:"flex", gap:10 }}>
              <Btn onClick={()=>setShowSignOutConfirm(false)} variant="ghost" style={{ flex:1, padding:"11px" }}>Cancel</Btn>
              <Btn onClick={()=>{ setShowSignOutConfirm(false); handleSignOut(); }} variant="primary" style={{ flex:1, padding:"11px" }}>Sign Out →</Btn>
            </div>
          </div>
        </div>
      )}
      {showEmailConfirm && <EmailConfirmModal member={showEmailConfirm} onClose={()=>{ setShowEmailConfirm(null); showToast("Welcome Back", showEmailConfirm.name.toUpperCase(), `Signed in as @${showEmailConfirm.username}`); }} />}
      {showEditProfile && currentUser && <EditProfileModal member={currentUser} members={members} onClose={()=>setShowEditProfile(false)} onSave={handleEditProfile} />}
      {showEmailPreview && <EmailPreview project={showEmailPreview.project} author={showEmailPreview.author} members={members} onClose={()=>setShowEmailPreview(null)} />}
      {toast          && <Toast msg={toast} onDismiss={()=>setToast(null)} />}
    </div>
  );
}
